import { http, HttpResponse, delay } from "msw";
import { showDemoDownloadModal, showDemoUploadModal } from "./demoModal";
import { demoMembers, demoUserId } from "./mockData/members";
import { demoWorkgroups } from "./mockData/workgroups";
import { thumbnails } from "./mockData/documents";
import {
  store_docs,
  store_trash,
  store_workgroups,
  store_meetings,
  getWorkgroupMap,
  getDocsByWorkgroup,
  getAllActiveDocs,
  getFavoriteDocs,
  findDoc,
  deleteDoc,
  restoreDoc,
  permanentDeleteDoc,
  renameDoc,
  toggleDocFavorite,
  addWorkgroup,
  deleteWorkgroup,
  renameWorkgroup,
  toggleWorkgroupFavorite,
  toggleMeetingFavorite,
  addMeeting,
  deleteMeeting,
  updateMeeting,
  getLinkPolicy,
  setLinkPolicy,
  setDocPermissions,
  getDocPermissions,
  addComment,
  getComments,
  incrementDocSignCount,
  addReadRequest,
  getReadRequests,
  deleteReadRequest,
  isReadRequestDeleted,
  store_folderTree,
  addFolder,
  updateFolder,
  getDocFolder,
  moveDocsToFolder,
} from "./store";
import { t } from "i18next";
// host + /eco-gateway 접두사를 무시하고 정확한 경로만 매칭 (쿼리 파라미터 허용)
const rx = (p: string) =>
  new RegExp(`^(https?:\\/\\/[^/]+)?(\\/eco-gateway)?${p}\\/?(?:[?#]|$)`);

// 경로 끝이 파라미터인 패턴 생성 (anchor 포함, 쿼리 파라미터 허용)
const rxPath = (...segments: string[]) => {
  const escaped = segments.map((s) =>
    s.startsWith(":") ? "([^/]+)" : s.replace(/\//g, "\\/")
  );
  return new RegExp(
    `^(https?:\\/\\/[^/]+)?(\\/eco-gateway)?${escaped.join(
      "\\/"
    )}\\/?(?:[?#]|$)`
  );
};

/** 문서의 기본 권한 목록 생성 (seed 기반) */
const buildDefaultPermissions = (doc: any): any[] => {
  const wgCode = doc.workgroup?.workgroupId ?? "NONEWORKGROUP";
  const now = Date.now();
  const day = 86400000;
  const seed = doc.syncId
    .split("")
    .reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0);
  const policyVariants = [
    {},
    { allowDays: 30, startTime: now - 2 * day },
    { download: false, revision: false, wrapping: false },
    { allowViewCount: 10, currentViewCount: 3 },
    { allowDownloadCount: 5, currentDownloadCount: 2 },
    { allowDays: 7, startTime: now - 4 * day },
    { revision: false, wrapping: false },
    { allowDays: 14, startTime: now - 5 * day },
    { allowViewCount: 20, currentViewCount: 11 },
    { download: false },
    { allowDays: 90, startTime: now - 30 * day },
    { revision: false },
    { allowDownloadCount: 3, currentDownloadCount: 3 },
  ];
  const basePolicy = {
    startTime: 0,
    allowDays: 0,
    allowDaysTimeZone: 9,
    allowViewCount: 0,
    currentViewCount: 0,
    allowDownloadCount: 0,
    currentDownloadCount: 0,
    drmPerm: [],
    webView: true,
    revision: true,
    download: true,
    wrapping: true,
    additionalViewPermission: "NONE",
    fileShareLinkPolicy: null,
    notificationPolicy: null,
    sendShareMail: false,
  };
  const masterUserId: string =
    doc.masterUser?.userId ?? doc.master?.userId ?? demoUserId;
  const masterMember = demoMembers.find((m) => m.userId === masterUserId);
  const picked = getPermittedMembers(doc.syncId);
  const workgroupEntry =
    wgCode !== "NONEWORKGROUP"
      ? [
          {
            changeCode: null,
            uniqueId: "WORKGROUP",
            workgroupCode: wgCode,
            syncId: doc.syncId,
            targetType: "WORKGROUP",
            targetValue: wgCode,
            authType: "LOGIN",
            policyType: "TEMPLATE",
            policyCode: "",
            updateTime: 0,
            createUser: demoUserId,
            updateUser: demoUserId,
            policy: { ...basePolicy },
          },
        ]
      : [];
  return [
    {
      changeCode: null,
      uniqueId: "MASTER",
      workgroupCode: wgCode,
      syncId: doc.syncId,
      targetType: "USER",
      targetValue: masterUserId,
      authType: "LOGIN",
      policyType: "DOCUMENT",
      policyCode: "",
      updateTime: 0,
      createUser: masterUserId,
      updateUser: masterUserId,
      companyName: "Wrapsody eCo Demo",
      deptName: masterMember?.deptName ?? "Product Team",
      userType: masterMember?.userType ?? 1,
      status: masterMember?.status ?? 1,
      userName: masterMember?.userName ?? masterUserId,
      phoneNumber: "",
      policy: { ...basePolicy },
    },
    ...workgroupEntry,
    ...picked.map((m: any, i: number) => {
      const uid = `${seed.toString(16).padStart(8, "0")}${i}${m.userId
        .replace(/\W/g, "")
        .slice(0, 8)}`;
      return {
        changeCode: null,
        uniqueId: uid,
        workgroupCode: wgCode,
        syncId: doc.syncId,
        targetType: "USER",
        targetValue: m.userId,
        authType: "LOGIN",
        policyType: "DOCUMENT",
        policyCode: uid,
        updateTime: now - day * (1 + i),
        createUser: demoUserId,
        updateUser: demoUserId,
        companyName: "Wrapsody eCo Demo",
        deptName: m.deptName,
        userType: m.userType,
        status: m.status,
        userName: m.userName,
        phoneNumber: "",
        policy: {
          ...basePolicy,
          ...policyVariants[(seed + i) % policyVariants.length],
        },
      };
    }),
  ];
};

/** syncId 기반으로 해당 문서의 권한 멤버 목록 반환 (MASTER 제외) */
const getPermittedMembers = (syncId: string) => {
  const seed = syncId.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const nonMaster = demoMembers.filter((m) => m.userId !== demoUserId);
  const shuffled = [...nonMaster];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = (seed * (i + 1) * 2654435761) % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, 2 + (seed % 5));
};

/** 알림 설정 in-memory store */
let store_noti = {
  usingWorkgroupInvitedAlert: true,
  usingFileAlert: true,
  fileAlert: ["fileshare", "memo", "revision"],
  usingReadRequestAlert: true,
  readRequestAlert: ["readRequest", "readRequestConfirm"],
};

/** 워크그룹 목록 응답 헬퍼 */
const makeWorkgroupListResp = (list = store_workgroups) => ({
  workgroupList: list,
  pageRange: 1,
  pageTotal: list.length,
  posPage: 1,
  rowRange: 50,
  total: list.length,
});

/** URL에서 경로 파라미터 추출 (rxPath 캡처 그룹 순서 기반) */
const capture = (
  params: Record<string, string | readonly string[] | undefined>,
  idx: number
): string => (Object.values(params)[idx] as string | undefined) ?? "";

// ── 폴더 구조 헬퍼 ────────────────────────────────────────────────────────────
// flat 트리: { id, parentId, name }. parentId = "ECO_WORKGROUP" → 루트
interface FolderNode {
  id: string;
  parentId: string;
  name: string;
}

const buildFolderResponse = (workgroupId: string, folderCode?: string) => {
  const now = Date.now();
  const adminUser = { userId: demoUserId, userName: "Demo User" };

  // 워크그룹 트리가 없으면 루트 + 기본 2개 폴더로 fallback
  const tree: FolderNode[] = store_folderTree[workgroupId] ?? [
    { id: workgroupId, parentId: "ECO_WORKGROUP", name: workgroupId },
    { id: `fd-${workgroupId}-01`, parentId: workgroupId, name: "Documents" },
    { id: `fd-${workgroupId}-02`, parentId: workgroupId, name: "Archive" },
  ];

  const rootNode = tree.find((f) => f.parentId === "ECO_WORKGROUP")!;

  const toDto = (node: FolderNode) => ({
    folderId: node.id,
    parentFolderId: node.parentId,
    workgroupId,
    folderName: node.name,
    createTime: now - 30 * 86400000,
    createUser: adminUser,
    updateTime: now - 7 * 86400000,
    updateUser: null,
  });

  // folderCode 없거나 루트 id와 동일 → 루트 기준
  const currentId = folderCode || rootNode.id;
  const currentNode = tree.find((f) => f.id === currentId) ?? rootNode;

  // 현재 폴더의 직접 자식 목록
  const children = tree.filter((f) => f.parentId === currentNode.id);

  // breadcrumb: ECO_WORKGROUP 직전까지 조상 경로 (현재 폴더 제외)
  const ancestors: FolderNode[] = [];
  let cursor = currentNode;
  while (cursor.parentId !== "ECO_WORKGROUP") {
    const parent = tree.find((f) => f.id === cursor.parentId);
    if (!parent) break;
    ancestors.unshift(parent);
    cursor = parent;
  }

  return {
    currntFolderList: children.map(toDto),
    parentFolderList: ancestors.map(toDto),
    folderInfo: toDto(currentNode),
  };
};

const DOC_ACTION_TYPES = [
  "view",
  "revision",
  "download",
  "add",
  "sign",
  "change",
  "link",
];

// actionType → filterDocument saveType
/** /papi/actionUrl/:uid/info 응답 빌더 — 링크 정책 변경이 즉시 반영됨 */
const buildActionUrlInfo = (
  uid: string,
  doc: any,
  policy: ReturnType<typeof getLinkPolicy>
) => ({
  uniqueId: uid,
  targetType: "GLOBAL",
  targetValue: "GLOBAL",
  urlAction: policy.urlAction, // 링크 설정 변경 시 바로 반영
  authType: policy.authType, // NONE | LOGIN | PASSWORD 그대로 전달
  makerId: demoUserId,
  entryTime: Date.now(),
  expiredTime:
    policy.allowDays > 0 ? Date.now() + policy.allowDays * 86400000 : 0,
  syncId: doc.syncId,
  revisionNo: doc.revisionNo,
  tenancyCode: "",
  uploadUid: null,
});

const DOC_SAVE_TYPE: Record<string, number> = {
  view: 7,
  revision: 1,
  download: 5,
  add: 0,
  sign: 2,
  change: 1,
  link: 10,
};

// logType + actionType → historyType, saveType
const resolveHistoryType = (
  logType: string,
  actionType: string
): { historyType: number; saveType: number } => {
  if (logType === "document")
    return { historyType: 1, saveType: DOC_SAVE_TYPE[actionType] ?? 7 };
  if (logType === "webMeeting")
    return {
      historyType: 0,
      saveType: actionType === "change" ? 1 : actionType === "delete" ? 2 : 0,
    };
  if (logType === "user") return { historyType: 2, saveType: 4 };
  if (logType === "workgroupRole") return { historyType: 2, saveType: 2 };
  // workgroup
  if (actionType === "add") return { historyType: 2, saveType: 0 };
  if (actionType === "succession") return { historyType: 2, saveType: 3 };
  return { historyType: 2, saveType: 1 };
};

// logId → log item 캐시 (detail 핸들러에서 조회)
const logItemCache = new Map<string, any>();

const buildWorkgroupLogs = (wg: any) => {
  const now = Date.now();
  const day = 86400000;
  const docs = getDocsByWorkgroup(wg.workgroupId).slice(0, 5);
  const isOriginal = demoWorkgroups.some(
    (w) => w.workgroupId === wg.workgroupId
  );

  const makeLog = (
    idx: number,
    doc: any,
    actionType: string,
    daysAgo: number
  ) => {
    const userId = doc.checkinUser?.userId || demoUserId;
    const userName = doc.checkinUser?.userName || "Demo User";
    const logDate = now - daysAgo * day;
    const logId = `demo-log-${idx}-${doc.syncId}`;
    const { historyType, saveType } = resolveHistoryType(
      "document",
      actionType
    );
    return {
      logId,
      userId,
      userName,
      workgroupId: wg.workgroupId,
      workgroupName: wg.workgroupName,
      logType: "document",
      logTypeDbValue: "document",
      actionType,
      actionTypeDbValue: actionType,
      logDatas: [],
      logData: null,
      logDate,
      entryDate: logDate,
      syncId: doc.syncId,
      documnetName: doc.contentName,
      result: null,
      status: null,
      saveType,
      historyType,
      revisionNo: doc.revisionNo,
      desc: null,
      ip: "192.168.1.100",
      agentType: 1,
      agentVersion: "3.0",
      policyLogs: null,
      children: [],
      firstDate: logDate,
      lastDate: logDate,
      logIds: [logId],
      totalCount: 1,
      documentName: doc.contentName,
      contentName: doc.contentName,
      involvedUsers: [{ userId, userName }],
      actionTypeCounts: { [`document_${actionType}`]: 1 },
      logDetailInfo: {
        logId,
        userId,
        userName,
        logType: "document",
        actionType,
        logDate,
        syncId: doc.syncId,
        documnetName: doc.contentName,
        revisionNo: doc.revisionNo,
      },
    };
  };

  const makeWgLog = (
    idx: number,
    actionType: string,
    daysAgo: number,
    extra?: any
  ) => {
    const logDate = now - daysAgo * day;
    const logId = `demo-wg-log-${wg.workgroupId}-${idx}`;
    const logType = extra?.logType ?? "workgroup";
    const logData = extra?.logData ?? null;
    const userId = extra?.userId ?? demoUserId;
    const userName = extra?.userName ?? "Demo User";
    const { historyType, saveType } = resolveHistoryType(logType, actionType);
    let topic: string | null = null;
    if (logData) {
      try {
        topic = JSON.parse(logData)?.topic ?? null;
      } catch {
        topic = null;
      }
    }
    return {
      logId,
      userId,
      userName,
      workgroupId: wg.workgroupId,
      workgroupName: wg.workgroupName,
      logType,
      logTypeDbValue: logType,
      actionType,
      actionTypeDbValue: actionType,
      logDatas: [],
      logData,
      logDate,
      entryDate: logDate,
      syncId: null,
      documnetName: null,
      result: null,
      status: null,
      saveType,
      historyType,
      revisionNo: 0,
      desc: logType === "webMeeting" ? topic : null,
      ip: "192.168.1.100",
      agentType: 1,
      agentVersion: "3.0",
      policyLogs: null,
      children: [],
      firstDate: logDate,
      lastDate: logDate,
      logIds: [logId],
      totalCount: 1,
      documentName: null,
      contentName: null,
      involvedUsers: [{ userId, userName }],
      actionTypeCounts: { [`${logType}_${actionType}`]: 1 },
      logDetailInfo: {
        logId,
        userId,
        userName,
        logType,
        actionType,
        logDate,
        logData,
      },
      ...extra,
    };
  };

  if (!isOriginal) {
    const single = makeWgLog(0, "add", 0);
    logItemCache.set(single.logId, single);
    return [single];
  }

  const docLogs = docs.flatMap((doc: any, i: number) => [
    makeLog(i * 2, doc, DOC_ACTION_TYPES[i % DOC_ACTION_TYPES.length], i + 1),
    makeLog(
      i * 2 + 1,
      doc,
      DOC_ACTION_TYPES[(i + 3) % DOC_ACTION_TYPES.length],
      i + 3
    ),
  ]);

  const wgLogs = [
    // 워크그룹장 승계
    makeWgLog(100, "succession", 1, {
      logType: "workgroup",
      logTypeDbValue: "workgroup",
      userId: demoUserId,
      userName: "Demo User",
      logData: `${demoMembers[1].userName}(${demoMembers[1].userId})`,
      desc: `Demo User(${demoUserId}) -> ${demoMembers[1].userName}(${demoMembers[1].userId})`,
      userType: 1,
      chatLogData: null,
      prevLogData: `Demo User(${demoUserId})`,
    }),
    // 워크그룹 정책 변경 (workgroupRole)
    makeWgLog(101, "change", 3, {
      logType: "workgroupRole",
      logTypeDbValue: "workgroupRole",
      userId: demoUserId,
      userName: "Demo User",
      desc: wg.workgroupName,
      policyLogs: [
        {
          title: null,
          permissionPolicy: {
            changeCode: "UPDATE",
            syncId: "NONE",
            targetType: "WORKGROUP",
            targetValue: wg.workgroupId,
            authType: "LOGIN",
            policyType: "TEMPLATE",
            policy: {
              allowDays: 0,
              webView: true,
              revision: true,
              download: false,
              wrapping: false,
              fileShareLinkPolicy: {
                authType: "NONE",
                password: "",
                urlAction: "WEBUSING",
                allowDays: 61,
                allowDaysTimeZone: 0,
              },
            },
          },
        },
      ],
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 워크그룹 내용 수정 (이름 변경)
    makeWgLog(102, "change", 5, {
      logType: "workgroup",
      logTypeDbValue: "workgroup",
      userId: demoUserId,
      userName: "Demo User",
      desc: `Edit name: Old ${wg.workgroupName} -> ${wg.workgroupName}`,
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 멤버 추가
    makeWgLog(103, "add", 7, {
      logType: "user",
      logTypeDbValue: "user",
      userId: demoUserId,
      userName: "Demo User",
      logDatas: [
        `${demoMembers[2].userName}(${demoMembers[2].userId})`,
        `${demoMembers[3].userName}(${demoMembers[3].userId})`,
      ],
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 멤버 삭제
    makeWgLog(104, "delete", 10, {
      logType: "user",
      logTypeDbValue: "user",
      userId: demoMembers[0].userId,
      userName: demoMembers[0].userName,
      logDatas: [`${demoMembers[4].userName}(${demoMembers[4].userId})`],
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 멤버 탈퇴
    makeWgLog(105, "withdraw", 14, {
      logType: "user",
      logTypeDbValue: "user",
      userId: demoMembers[2].userId,
      userName: demoMembers[2].userName,
      logDatas: [`${demoMembers[2].userName}(${demoMembers[2].userId})`],
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 워크그룹 설명 수정
    makeWgLog(106, "change", 18, {
      logType: "workgroup",
      logTypeDbValue: "workgroup",
      userId: demoUserId,
      userName: "Demo User",
      desc: "Edit description: Demo workgroup for testing purposes",
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 화상회의 추가
    makeWgLog(107, "add", 20, {
      logType: "webMeeting",
      logTypeDbValue: "webMeeting",
      actionType: "add",
      actionTypeDbValue: "add",
      userId: demoUserId,
      userName: "Demo User",
      logData: JSON.stringify({
        topic: "Q2 Design Review",
        meetingId: "mtg-001",
      }),
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
    // 화상회의 수정
    makeWgLog(108, "change", 25, {
      logType: "webMeeting",
      logTypeDbValue: "webMeeting",
      actionType: "change",
      actionTypeDbValue: "change",
      userId: demoMembers[1].userId,
      userName: demoMembers[1].userName,
      logData: JSON.stringify({
        topic: "Sprint Planning",
        meetingId: "mtg-002",
      }),
      userType: 1,
      chatLogData: null,
      prevLogData: null,
    }),
  ];

  const allLogs = [...docLogs, ...wgLogs].sort(
    (a, b) => (b.logDate ?? 0) - (a.logDate ?? 0)
  );
  allLogs.forEach((item) => {
    if (item.logId) logItemCache.set(item.logId, item);
  });
  return allLogs;
};

export const demoHandlers = [
  // ──────────────────────────────────────────
  // 알람
  // ──────────────────────────────────────────
  http.post(/wapi\/dashboard\/myAlarms/, async ({ request }) => {
    await delay(100);
    const now = Date.now();
    const day = 86400000;
    const alarmResults = [
      {
        logId: "alarm-001",
        alarmType: "share",
        senderId: "alice.johnson",
        senderName: "Alice Johnson",
        receiverId: demoUserId,
        receiverName: "Demo User",
        syncId: "doc-eng-001",
        contentName: "Architecture Decision Record - API Gateway.docx",
        workgroupId: "wg-engineering-core",
        workgroupName: "Engineering Core",
        msg: "",
        alarmStatus: "S",
        logDate: now - 1 * 3600000,
        updateDate: now - 1 * 3600000,
        totalCount: 1,
        unreadCount: 1,
        userType: 1,
      },
      {
        logId: "alarm-002",
        alarmType: "memo",
        senderId: "bob.chen",
        senderName: "Bob Chen",
        receiverId: demoUserId,
        receiverName: "Demo User",
        syncId: "doc-eng-003",
        contentName: "Database Schema v3.xlsx",
        workgroupId: "wg-engineering-core",
        workgroupName: "Engineering Core",
        msg: "The schema for Table 3 has been updated with additional columns. Please review.",
        alarmStatus: "S",
        logDate: now - 3 * 3600000,
        updateDate: now - 3 * 3600000,
        totalCount: 1,
        unreadCount: 1,
        userType: 1,
      },
      {
        logId: "alarm-003",
        alarmType: "invite",
        senderId: "carol.smith",
        senderName: "Carol Smith",
        receiverId: demoUserId,
        receiverName: "Demo User",
        syncId: "",
        contentName: "",
        workgroupId: "wg-marketing-campaign",
        workgroupName: "Marketing Campaign",
        msg: "",
        alarmStatus: "N",
        logDate: now - 1 * day,
        updateDate: now - 1 * day,
        totalCount: 1,
        unreadCount: 0,
        userType: 1,
      },
    ];
    const body: any = await request.json().catch(() => ({}));

    return HttpResponse.json({
      userId: demoUserId,
      firstLogId: "alarm-001",
      lastLogId: "alarm-003",
      totalCount: alarmResults.length,
      count: alarmResults.length,
      alarmResults: body.logId === "" ? alarmResults : [],
      unreadCount: 2,
    });
  }),

  http.post(rx("/wapi/dashboard/readAlarm"), async () => {
    await delay(100);
    return HttpResponse.json({
      userId: demoUserId,
      count: 0,
      alarmResults: [],
    });
  }),

  http.post(rx("/wapi/dashboard/readAllAlarm"), async () => {
    await delay(100);
    return HttpResponse.json({ code: "200", message: "OK" });
  }),

  // 홈 대시보드 tasks / suggestions
  http.get(rx("/wapi/dashboard/tasks"), async () => {
    await delay(100);
    const now = Date.now();
    const day = 86400000;
    return HttpResponse.json({
      tasks: [
        {
          type: "invite",
          data: {
            logId: "demo-task-invite-001",
            alarmType: "invite",
            senderId: "alice.johnson",
            senderName: "Alice Johnson",
            receiverId: demoUserId,
            receiverName: "Demo User",
            syncId: null,
            contentName: null,
            workgroupId: "wg-product-design",
            workgroupName: "Product Design",
            msg: null,
            alarmStatus: "S",
            logDate: now - day * 2,
            updateDate: now - day * 2,
            totalCount: 0,
            unreadCount: 0,
            userType: 1,
            managerId: "alice.johnson",
            managerName: "Alice Johnson",
          },
        },
        {
          type: "share",
          data: {
            logId: "demo-task-share-001",
            alarmType: "share",
            senderId: "carol.smith",
            senderName: "Carol Smith",
            receiverId: demoUserId,
            receiverName: "Demo User",
            syncId: "doc-mkt-001",
            contentName: "Q3 Campaign Brief.pptx",
            workgroupId: "wg-marketing-campaign",
            workgroupName: "Marketing Campaign",
            msg: "",
            alarmStatus: "S",
            logDate: now - day,
            updateDate: now - day,
            totalCount: 0,
            unreadCount: 0,
            userType: 1,
            managerId: "carol.smith",
            managerName: "Carol Smith",
          },
        },
        {
          type: "invite",
          data: {
            logId: "demo-task-invite-002",
            alarmType: "invite",
            senderId: "grace.lee",
            senderName: "Grace Lee",
            receiverId: demoUserId,
            receiverName: "Demo User",
            syncId: null,
            contentName: null,
            workgroupId: "wg-executive-projects",
            workgroupName: "Executive Projects",
            msg: null,
            alarmStatus: "S",
            logDate: now - 3600000 * 5,
            updateDate: now - 3600000 * 5,
            totalCount: 0,
            unreadCount: 0,
            userType: 1,
            managerId: "grace.lee",
            managerName: "Grace Lee",
          },
        },
      ],
    });
  }),

  http.get(rx("/wapi/dashboard/suggestions"), async () => {
    await delay(100);
    return HttpResponse.json({ tasks: [] });
  }),

  // ──────────────────────────────────────────
  // 워크스페이스 설정 (tenancy config)
  // ──────────────────────────────────────────
  http.get(rx("/wapi/tenancy/config"), async () => {
    await delay(100);
    return HttpResponse.json({
      externalUserViewType: "ALL",
      webOfficeAllowUserType: "ALL",
      restorePermission: "ALL",
      unwrappingPermission: "NONE",
      watermark: "ALL",
      fileUploadMaxSizeMB: 200,
      fileUploadConfigMaxSizeMB: 200,
      licenseAlertPercentage: 80,
      chatAvailibility: "true",
      sentMail: false,
      passwordPolicyDTO: { minLength: 6, maxLength: 30 },
      lastUpdateTime: Date.now(),
      serviceType: "CLOUD",
      allowMobile: true,
      allowMacOS: true,
      watermarkPattern:
        "${username}(${userid}) / ${contentname} / ${currenttime} / aaa",
      workgroupCreatePermission: "EXTERNAL",
      workgroupHeadSelectPermission: "ALL",
      fileUploadPermission: "EXTERNAL",
      fileOwnerSelectPermission: "ALL",
      autoUnwrappingPermission: false,
      activeHtmlWrapping: false,
      userOtpType: "NONE",
      watermarkTop: 59,
      watermarkFontSize: 16,
      watermarkOpacity: 0.2,
      watermarkRotate: 30,
      WebOfficeEditAllowUserType: "ALL",
      webOfficeType: "NONE",
      workgroupTreeViewPermission: "ALL",
      workgroupTreeCreatePermission: "MASTER",
    });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 목록
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/simpleList"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    let list = store_workgroups;
    if (body.workgroupName) {
      const q = body.workgroupName.toLowerCase();
      list = store_workgroups.filter((wg) =>
        wg.workgroupName.toLowerCase().includes(q)
      );
    }
    return HttpResponse.json(makeWorkgroupListResp(list));
  }),

  http.post(rx("/wapi/workgroups/sideList"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    let list = [...store_workgroups];
    if (body.workgroupName) {
      const q = body.workgroupName.toLowerCase();
      list = list.filter((wg) => wg.workgroupName.toLowerCase().includes(q));
    }
    const orderBy: string = body.orderBy ?? "";
    const orderByType: string = (body.orderByType ?? "ASC").toUpperCase();
    if (orderBy === "workgroupName") {
      list = list.sort((a, b) => {
        const cmp = a.workgroupName.localeCompare(b.workgroupName, "en", {
          numeric: true,
        });
        return orderByType === "DESC" ? -cmp : cmp;
      });
    } else if (orderBy === "searchDate" || orderBy === "updateDate") {
      list = list.sort((a, b) => {
        const cmp = (a.updateDate ?? 0) - (b.updateDate ?? 0);
        return orderByType === "DESC" ? -cmp : cmp;
      });
    }
    return HttpResponse.json(makeWorkgroupListResp(list));
  }),

  http.post(rx("/wapi/workgroups/list"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    let list = [...store_workgroups];
    if (body.workgroupName) {
      const q = body.workgroupName.toLowerCase();
      list = list.filter((wg) => wg.workgroupName.toLowerCase().includes(q));
    }
    if (body.searchDate?.start > 0) {
      list = list.filter((wg) => (wg.updateDate ?? 0) >= body.searchDate.start);
    }
    if (body.searchDate?.end > 0) {
      list = list.filter((wg) => (wg.updateDate ?? 0) <= body.searchDate.end);
    }
    if (body.searchMemberIds?.length > 0) {
      const ids = new Set<string>(body.searchMemberIds);
      list = list.filter((wg) => wg.members?.some((m) => ids.has(m.userId)));
    }
    const orderBy: string = body.orderBy ?? "";
    const orderByType: string = (body.orderByType ?? "ASC").toUpperCase();
    if (orderBy === "workgroupName") {
      list = list.sort((a, b) => {
        const cmp = a.workgroupName.localeCompare(b.workgroupName, "en", {
          numeric: true,
        });
        return orderByType === "DESC" ? -cmp : cmp;
      });
    } else if (orderBy === "updateDate" || orderBy === "searchDate") {
      list = list.sort((a, b) => {
        const cmp = (a.updateDate ?? 0) - (b.updateDate ?? 0);
        return orderByType === "DESC" ? -cmp : cmp;
      });
    }
    return HttpResponse.json(makeWorkgroupListResp(list));
  }),

  // ──────────────────────────────────────────
  // 최근 방문 워크그룹 (Home 페이지)
  // ──────────────────────────────────────────
  http.get(rx("/wapi/workgroups/recent/list"), async () => {
    await delay(50);
    return HttpResponse.json(store_workgroups.slice(0, 5));
  }),

  // ──────────────────────────────────────────
  // 워크그룹 즐겨찾기 토글
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/favorite"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    if (body.users?.length > 0) {
      toggleWorkgroupFavorite(body.workgroupId, body.users[0].favorite);
    }
    return HttpResponse.json(store_workgroups);
  }),

  // ──────────────────────────────────────────
  // 워크그룹 상세 (POST /wapi/workgroups)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const wgMap = getWorkgroupMap();
    const wg = wgMap[body.workgroupId] ?? store_workgroups[0];
    return HttpResponse.json({
      workgroup: wg,
      policy: {
        workgroupCode: wg.workgroupId,
        syncId: "NONE",
        targetType: "WORKGROUP",
        authType: "LOGIN",
        policyType: "TEMPLATE",
        updateTime: Date.now() - 86400000,
        createUser: wg.managerId,
        updateUser: wg.managerId,
        policy: {
          allowDays: 30,
          allowDaysTimeZone: 9.0,
          allowViewCount: 0,
          currentViewCount: 0,
          allowDownloadCount: 0,
          currentDownloadCount: 0,
          drmPerm: [],
          webView: true,
          revision: true,
          download: true,
          wrapping: true,
          additionalViewPermission: "INTERNAL_USER",
          fileShareLinkPolicy: {
            authType: "NONE",
            password: "",
            urlAction: "DOWNLOAD",
            allowDays: 61,
            allowDaysTimeZone: 0.0,
          },
          notificationPolicy: {
            usingWorkgroupInvitedAlert: true,
            usingFileAlert: true,
            fileAlert: ["fileshare", "memo", "revision"],
            usingReadRequestAlert: true,
            readRequestAlert: ["readRequest", "readRequestConfirm"],
          },
          sendShareMail: false,
        },
      },
    });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 홈 대시보드 (GET /wapi/workgroups/home/dashboard/{workgroupId})
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/workgroups/home/dashboard", ":workgroupId"),
    async ({ params }) => {
      await delay(200);
      const workgroupId = capture(params, 2); // 0: host, 1: eco-gateway, 2: workgroupId
      const docs = getDocsByWorkgroup(workgroupId);
      return HttpResponse.json({
        readRequestCount: 0,
        todayUpdateFileCount: docs.length > 0 ? 3 : 0,
        todayMeeringCount: 0,
        unreadChat: 0,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 워크그룹 달력
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/workgroups/calendar", ":workgroupId", "count"),
    async () => {
      await delay(100);
      return HttpResponse.json([]);
    }
  ),

  http.get(
    rxPath("/wapi/workgroups/calendar", ":workgroupId", "detail"),
    async () => {
      await delay(100);
      return HttpResponse.json({
        dateInfo: { day: 1, eventDate: Date.now(), month: 3, year: 2026 },
        events: { meetings: [], signs: null, readRequests: null },
      });
    }
  ),

  // ──────────────────────────────────────────
  // 즐겨찾기 아이템 목록 (Home, Favorite 페이지)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/favorite/item/list/total"), async () => {
    await delay(200);
    const favDocItems = getFavoriteDocs().map((doc) => ({
      type: "CONTENT",
      content: doc,
      meeting: null,
    }));
    const favMeetingItems = store_meetings
      .filter((m) => m.favorite === "Y")
      .map((m) => ({
        type: "MEETING",
        content: null,
        meeting: m,
      }));
    const favoriteTotalItems = [...favMeetingItems, ...favDocItems];
    return HttpResponse.json({
      date: { start: 0, end: Date.now() },
      order: "datedesc",
      resultSize: 20,
      workgroupIds: [],
      favoriteTotalItems,
      meetingPage: 0,
      contentPage: 0,
      meetingPos: favMeetingItems.length,
      contentPos: favDocItems.length,
    });
  }),
  // ──────────────────────────────────────────
  // 문서 이름 변경 (POST /wapi/contents/docname)
  // ──────────────────────────────────────────

  http.post(rx("/wapi/contents/docname"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const targetDocSyncId = body.syncId;
    const newName = body.contentName;
    if (targetDocSyncId && newName) renameDoc(targetDocSyncId, newName);
    return HttpResponse.json({
      contentName: newName,
      syncId: targetDocSyncId,
      userId: demoUserId,
      ver: "1",
    });
  }),
  // ──────────────────────────────────────────
  // 문서 목록 (POST /wapi/contents/list)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/list"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));

    // tagCodes: 워크그룹 ID 배열. 여러 개면 OR 필터. 없으면 전체.
    const tagCodes: string[] = body.workgroupId
      ? [body.workgroupId]
      : Array.isArray(body.tagCodes) && body.tagCodes.length > 0
      ? body.tagCodes
      : [];
    let docs =
      tagCodes.length === 0
        ? getAllActiveDocs()
        : tagCodes.length === 1
        ? getDocsByWorkgroup(tagCodes[0])
        : tagCodes.flatMap((id) => getDocsByWorkgroup(id));

    if (body.readType === "favorit") {
      docs = getFavoriteDocs();
    } else if (body.readType === "trash") {
      docs = [...store_trash];
    } else if (body.readType === "unread") {
      // 내가 관리자인 문서 제외
      docs = docs.filter((d: any) => d.masterUser?.userId !== demoUserId);
      // 나머지에서 간헐적으로 표시 (syncId 기반 결정적 랜덤)
      docs = docs.filter((d: any) => {
        const hash = d.syncId
          .split("")
          .reduce((acc: number, c: string) => acc + c.charCodeAt(0), 0);
        return hash % 3 !== 0;
      });
    }

    // masterUserId 필터
    // 특정 userId: 해당 유저가 master인 문서만
    // "-1": 해당 유저가 master가 아닌 문서만 (타인 소유 문서)
    const masterUserId: string = body.masterUserId ?? "";
    if (masterUserId === "-1") {
      docs = docs.filter((d) => (d as any).masterUser?.userId !== demoUserId);
    } else if (masterUserId) {
      docs = docs.filter((d) => (d as any).masterUser?.userId === masterUserId);
    }

    if (body.search) {
      const q = body.search.toLowerCase();
      docs = docs.filter((d) => d.contentName.toLowerCase().includes(q));
    }
    if (body.tagCodes && body.tagCodes.length > 0) {
      docs = docs.filter((d) =>
        body.tagCodes.includes(d.workgroup.workgroupId)
      );
    }
    if (body.date) {
      if (body.date.start) {
        docs = docs.filter((d) => body.date.start <= d.updateTime);
      }
      if (body.date.end) {
        docs = docs.filter((d) => body.date.end >= d.updateTime);
      }
    }
    // order 정렬 처리
    const order: string = body.order ?? "datedesc";
    docs = [...docs].sort((a, b) => {
      switch (order) {
        case "name":
        case "file":
          return a.contentName.localeCompare(b.contentName);
        case "namedesc":
        case "filedesc":
          return b.contentName.localeCompare(a.contentName);
        case "date":
          return (a.revisionDate ?? 0) - (b.revisionDate ?? 0);
        case "datedesc":
        default:
          return (b.revisionDate ?? 0) - (a.revisionDate ?? 0);
      }
    });

    const workgroupId: string = body.workgroupId ?? tagCodes[0] ?? "";
    const folderCode: string | undefined = body.folderCode ?? undefined;

    // 폴더 필터링: subfolder면 해당 폴더 docs만, root면 미배정 docs만
    if (
      (!body.readType || body.readType === "unread") &&
      !body.search &&
      folderCode
    ) {
      const isRoot = !!store_folderTree[folderCode]; // key가 workgroupId이면 root
      if (isRoot) {
        docs = docs.filter((d) => !getDocFolder(d.syncId));
      } else {
        docs = docs.filter((d) => getDocFolder(d.syncId) === folderCode);
      }
    }

    const showFolder =
      workgroupId &&
      workgroupId !== "NONEWORKGROUP" &&
      (!body.readType || body.readType === "unread") &&
      !body.search;

    return HttpResponse.json({
      folder: showFolder ? buildFolderResponse(workgroupId, folderCode) : null,
      pages: { nextPage: -1, prevPage: -1, page: 0 },
      total: docs.length,
      version: "1",
      docs: { count: docs.length, doc: docs },
    });
  }),

  // ──────────────────────────────────────────
  // 문서 버전 이력 (timeline) — detail보다 먼저 등록해야 함
  // GET /wapi/contents/detail/timeline/{workgroupId}/{syncId}
  // 반환: DocumentLogResult
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/contents/detail/timeline", ":workgroupId", ":syncId"),
    async ({ params }) => {
      await delay(50);
      const syncId = capture(params, 3);
      const doc = findDoc(syncId) ?? store_docs[0];
      const wgId = doc.workgroup?.workgroupId ?? "";
      const wgName = doc.workgroup?.workgroupName ?? "";

      const makeTimelineLog = (
        logType: string,
        actionType: string,
        saveType: number,
        member: { userId: string; userName: string },
        baseDate: number,
        offsetMs: number,
        revNo: number,
        policyLogs: any = null,
        logData: string | null = null
      ) => {
        const logDate = baseDate + offsetMs;
        const id = `demo-tl-${syncId}-${revNo}-${actionType}-${member.userId}-${offsetMs}`;
        return {
          logId: id,
          userId: member.userId,
          userName: member.userName,
          workgroupId: wgId,
          workgroupName: wgName,
          logType,
          logTypeDbValue: logType,
          actionType,
          actionTypeDbValue: actionType,
          logDatas: null,
          logData,
          logDate,
          entryDate: Math.floor(logDate / 1000) * 1000,
          syncId: doc.syncId,
          documnetName: doc.contentName,
          result: null,
          status: null,
          saveType,
          historyType: 0,
          revisionNo: revNo,
          desc: "",
          ip: "192.168.0.1",
          agentType: 3,
          agentVersion: "Windows-Chrome-144.0.0.0",
          policyLogs,
          userType:
            demoMembers.find((m) => m.userId === member.userId)?.userType ?? 2,
          chatLogData: null,
          prevLogData: null,
        };
      };

      const timelineList = Array.from({ length: doc.revisionNo }, (_, i) => {
        const revNo = doc.revisionNo - i;
        const baseDate = (doc.revisionDate ?? Date.now()) - i * 86400000 * 3;
        const master = {
          userId: doc.checkinUser?.userId ?? demoUserId,
          userName: doc.checkinUser?.userName ?? "Demo User",
        };
        const wg = getWorkgroupMap()[wgId];
        const pagingLogs = wg ? buildWorkgroupLogs(wg) : [];
        const logs: any[] = pagingLogs
          .filter((l: any) => l.syncId === doc.syncId)
          .map((l: any) => ({
            ...l,
            revisionNo: revNo,
            userType:
              demoMembers.find((m: any) => m.userId === l.userId)?.userType ??
              2,
          }));

        return {
          revisionMap: {
            no: revNo,
            contentName: doc.contentName,
            memo: i === 0 ? "Latest version" : `Version ${revNo}`,
            regDate: baseDate,
            checkinUser: { userId: master.userId, userName: master.userName },
            delStatus: "N",
            fileSize: doc.fileSize,
            signs: { count: 0, sign: null },
            concurrentFlag: "N",
            concurrentUserList: [],
          },
          log: logs.sort((a, b) => b.logDate - a.logDate),
        };
      });

      return HttpResponse.json({
        timelineList,
        minNo: 1,
        maxNo: doc.revisionNo,
        total: doc.revisionNo,
        nextPage: -1,
        pageNo: 0,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 문서 상세 (GET /wapi/contents/detail/{workgroupId}/{syncId})
  // 반환: DocDetailDto
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/contents/detail", ":workgroupId", ":syncId"),
    async ({ params }) => {
      await delay(50);
      const syncId = capture(params, 3); // 0: host, 1: eco-gateway, 2: workgroupId, 3: syncId
      const doc = findDoc(syncId) ?? store_docs[0];
      return HttpResponse.json({
        documentInfo: doc,
        documentHistory: {
          ver: "1",
          syncId: doc.syncId,
          total: doc.revisionNo,
          nextcursor: -1,
          nextRevisionNo: -1,
          revisions: {
            count: doc.revisionNo,
            revision: [
              {
                no: doc.revisionNo,
                contentName: doc.contentName,
                memo: "Latest version",
                regDate: doc.revisionDate ?? Date.now(),
                checkinUser: {
                  userId: doc.checkinUser.userId,
                  userName: doc.checkinUser.userName,
                },
                delStatus: "N",
                fileSize: doc.fileSize,
                signs: { count: 0, sign: null },
                concurrentFlag: "N",
                concurrentUserList: [],
              },
            ],
          },
        },
        folderInfo: [],
        permissionPolicyList:
          getDocPermissions(doc.syncId) ?? buildDefaultPermissions(doc),
        timeline: null,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 문서 유저별 조회수
  // GET /wapi/contents/viewcount/{workgroupId}/{syncId}
  // ──────────────────────────────────────────
  http.get(
    /\/wapi\/contents\/viewcount\/([^/]+)\/([^/]+)/,
    async ({ params }) => {
      await delay(50);
      const parts = Object.values(params) as string[];
      const syncId = parts[1];
      const permissions = getDocPermissions(syncId) ?? [];
      const result = permissions
        .filter((p: any) => p.targetType === "USER" && p.targetValue)
        .map((p: any) => ({
          userId: p.targetValue,
          allowViewCount: p.policy?.allowViewCount ?? 0,
          currentViewCount: p.policy?.currentViewCount ?? 0,
        }));
      return HttpResponse.json(result);
    }
  ),

  // ──────────────────────────────────────────
  // 문서 syncInfo (편집모드 진입 전 체크)
  // GET /wapi/contents/syncInfo/{workgroupId}/{syncId}
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/contents/syncInfo", ":workgroupId", ":syncId"),
    async ({ params }) => {
      await delay(100);
      const syncId = capture(params, 3);
      const doc = findDoc(syncId) ?? store_docs[0];
      return HttpResponse.json({
        ver: "1",
        syncId: doc.syncId,
        syncid: doc.syncId,
        revisionNo: doc.revisionNo,
        status: "N",
        authType: "R",
        master: {
          userId: doc.checkinUser.userId,
          userName: doc.checkinUser.userName,
        },
        concurrentList: { userId: "", userName: null },
        concurrentLogList: { userId: "", userName: null },
        checkoutUser: { userId: "", userName: null },
        checkoutAuthList: { userId: "", userName: null },
        checkoutDeptAuthList: { dept: [] },
        viewAuthAllUsers: true,
        viewAuthList: { userId: "", userName: null },
      });
    }
  ),

  // ──────────────────────────────────────────
  // 웹오피스 열기 (미리보기/편집)
  // GET /wapi/contents/webusing/{permission}/{workgroupId}/{syncId}/{revisionNo}
  // ──────────────────────────────────────────
  http.get(
    /\/wapi\/contents\/webusing\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)/,
    async ({ params }) => {
      await delay(50);
      const parts = Object.values(params) as string[];
      const syncId = parts[2];
      const doc = findDoc(syncId) ?? store_docs[0];
      return HttpResponse.json({
        accessUrl: "/demo-preview.html",
        syncId: doc.syncId,
        revisionNo: doc.revisionNo,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 문서 조회수
  // GET /wapi/contents/viewcount/{workgroupId}/{syncId}
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/contents/viewcount", ":workgroupId", ":syncId"),
    async () => {
      await delay(100);
      return HttpResponse.json({ viewCount: Math.floor(Math.random() * 50) });
    }
  ),

  // ──────────────────────────────────────────
  // 문서 즐겨찾기 (flag: boolean 버전)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/favorite"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    if (body.syncIds) {
      // flag boolean 버전
      body.syncIds.forEach((sid: string) => toggleDocFavorite(sid, body.flag));
    } else if (body.syncId) {
      // syncId string 버전
      toggleDocFavorite(body.syncId, body.favorite ?? "Y");
    }
    const syncIds: string[] =
      body.syncIds ?? (body.syncId ? [body.syncId] : []);
    return HttpResponse.json({ flag: body.flag ?? true, syncIds });
  }),

  // ──────────────────────────────────────────
  // 문서 삭제 / 복원
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/delete"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const syncIds: string[] =
      body.syncIds ?? (body.syncId ? [body.syncId] : []);
    deleteDoc(syncIds);
    // WrapsodySetFedTagRequest 형식으로 반환 (Redux reducer에서 result.syncIds 사용)
    return HttpResponse.json({
      ver: "1",
      syncIds,
      userId: demoUserId,
      status: "D",
    });
  }),

  http.post(rx("/wapi/contents/restore"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const syncIds: string[] =
      body.syncIds ?? (body.syncId ? [body.syncId] : []);
    restoreDoc(syncIds);
    return HttpResponse.json({ ver: "1.0", fails: [] });
  }),
  http.post(rx("/wapi/contents/delete/now"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const syncIds: string[] =
      body.syncIds ?? (body.syncId ? [body.syncId] : []);
    permanentDeleteDoc(syncIds);
    return HttpResponse.json({ ver: "1.0", fails: [] });
  }),

  // ──────────────────────────────────────────
  // 다운로드 차단
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/downloadCheck"), async ({ request }) => {
    await delay(100);
    showDemoDownloadModal();
    const syncIds = await request.json().catch(() => []);
    return HttpResponse.json(syncIds, { status: 200 });
  }),

  // ──────────────────────────────────────────
  // 체크아웃 차단 (다운로드와 동일 처리)
  // POST /wapi/contents/checkout/:workgroupId/:syncId/:revisionNo
  // POST /wapi/contents/undocheckout/:workgroupId/:syncId/:revisionNo
  // ──────────────────────────────────────────
  http.post(/\/wapi\/contents\/checkout\//, async () => {
    await delay(100);
    showDemoDownloadModal();
    return new HttpResponse(null, { status: 200 });
  }),
  http.post(/\/wapi\/contents\/undocheckout\//, async () => {
    await delay(100);
    showDemoDownloadModal();
    return new HttpResponse(null, { status: 200 });
  }),

  // ──────────────────────────────────────────
  // 실제 다운로드 URL 차단 (document.ts reducer 크래시 방지)
  // GET /wapi/contents/download/:syncId/revisions/:revisionNo[/decrypt]
  // POST /wapi/contents/download/
  // ──────────────────────────────────────────
  http.get(/\/wapi\/contents\/download\//, async () => {
    await delay(100);
    showDemoDownloadModal();
    return new HttpResponse(null, { status: 403 });
  }),
  http.post(/\/wapi\/contents\/download\//, async () => {
    await delay(100);
    showDemoDownloadModal();
    return new HttpResponse(null, { status: 403 });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 전송 (delivery)
  // POST /wapi/contents/upload/:uid/delivery
  // POST /wapi/contents/upload/:uid/complete/delivery
  // ──────────────────────────────────────────
  http.post(/\/wapi\/contents\/upload\/[^/]+\/complete\/delivery/, async () => {
    await delay(100);
    return HttpResponse.json({}, { status: 200 });
  }),
  http.post(/\/wapi\/contents\/upload\/[^/]+\/delivery/, async () => {
    await delay(100);
    return HttpResponse.json({}, { status: 200 });
  }),

  // ──────────────────────────────────────────
  // 업로드 차단
  // POST /wapi/contents/upload/:uid/complete
  // POST /wapi/contents/upload/:uid/complete/:syncId
  // ──────────────────────────────────────────
  http.post(/\/wapi\/contents\/upload\/[^/]+\/complete/, async () => {
    await delay(100);
    showDemoUploadModal();
    return HttpResponse.json(
      { error: t("common.noDemoVersion") },
      { status: 403 }
    );
  }),

  // ──────────────────────────────────────────
  // 폴더 단건 조회
  // GET /wapi/contents/folder/info/:folderId
  // ──────────────────────────────────────────
  http.get(/\/wapi\/contents\/folder\/info\/([^/]+)/, async ({ params }) => {
    await delay(50);
    const folderId = (Object.values(params)[0] as string) ?? "";

    for (const [workgroupId] of Object.entries(store_folderTree)) {
      const result = buildFolderResponse(workgroupId, folderId);
      if (result.folderInfo.folderId === folderId) {
        return HttpResponse.json(result);
      }
    }
    return HttpResponse.json(null);
  }),

  // ──────────────────────────────────────────
  // 폴더 생성
  // POST /wapi/contents/folder/create
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/folder/create"), async ({ request }) => {
    await delay(100);
    const body: any = await request.json().catch(() => ({}));
    const workgroupId: string = body.workgroupId ?? "";
    const parentFolderId: string = body.parentFolderId ?? workgroupId;
    const folderName: string = body.folderName ?? "New Folder";
    const folderId = `fd-${workgroupId.slice(0, 6)}-${Date.now()}`;
    const now = Date.now();

    addFolder(workgroupId, {
      id: folderId,
      parentId: parentFolderId,
      name: folderName,
    });

    return HttpResponse.json({
      folderId,
      parentFolderId,
      workgroupId,
      folderName,
      createTime: now,
      createUser: { userId: demoUserId, userName: "Demo User" },
      updateTime: now,
      updateUser: null,
    });
  }),

  // ──────────────────────────────────────────
  // 폴더 수정 (폴더명 변경 등)
  // POST /wapi/contents/folder/update
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/folder/update"), async ({ request }) => {
    await delay(100);
    const body: any = await request.json().catch(() => ({}));
    const workgroupId: string = body.workgroupId ?? "";
    const folderId: string = body.folderId ?? "";
    const folderName: string = body.folderName ?? "";
    const now = Date.now();

    const node = updateFolder(workgroupId, folderId, folderName);

    return HttpResponse.json({
      folderId,
      parentFolderId: node?.parentId ?? workgroupId,
      workgroupId,
      folderName: node?.name ?? folderName,
      createTime: now - 30 * 86400000,
      createUser: { userId: demoUserId, userName: "Demo User" },
      updateTime: now,
      updateUser: { userId: demoUserId, userName: "Demo User" },
    });
  }),

  // ──────────────────────────────────────────
  // 파일 폴더 이동
  // POST /wapi/contents/folder/file/update
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/folder/file/update"), async ({ request }) => {
    await delay(100);
    const body: any = await request.json().catch(() => ({}));
    const workgroupId: string = body.workgroupId ?? "";
    const folderId: string = body.folderId ?? "";
    const syncIds: string[] = body.syncIds ?? [];
    moveDocsToFolder(syncIds, folderId);
    return HttpResponse.json({
      workgroupId,
      folderId,
      successSyncIds: syncIds,
      failSyncIds: [],
    });
  }),

  // ──────────────────────────────────────────
  // 열람 요청
  // ──────────────────────────────────────────
  http.get(rx("/wapi/contents/request/read/unread"), async () => {
    await delay(100);
    return HttpResponse.json({ docs: [], pos: 0, posCount: 0, totalCount: 0 });
  }),
  http.get(/\/wapi\/contents\/request\/read\/([^/]+)$/, async ({ request }) => {
    await delay(100);
    const url = new URL(request.url);
    const syncId =
      url.pathname.split("/wapi/contents/request/read/")[1]?.split("/")[0] ??
      "";
    const now = Date.now();
    const day = 86400000;
    const seed = syncId.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);

    // store에 저장된 요청 먼저 반환
    const stored = getReadRequests(syncId).map((r) => {
      const members = r.targetUserList
        .map((uid) => demoMembers.find((m) => m.userId === uid))
        .filter(Boolean) as (typeof demoMembers)[0][];
      return {
        syncId: r.syncId,
        requestId: r.requestId,
        requestUser: r.requestUser,
        targetUserList: members.map((m) => ({
          syncId: r.syncId,
          requestId: r.requestId,
          requestUser: r.requestUser,
          targetUser: m.userId,
          targetUserStatus: 1,
          targetUserType: m.userType,
          targetUserName: m.userName,
          targetUserEmail: m.email,
          requestTime: r.requestTime,
          requestInterval: r.requestInterval,
          reRequestTime: r.reRequestTime,
          comment: r.comment,
          mailLocale: "ko",
          entryTime: r.requestTime - 1000,
          requestSendTime: r.requestTime + 500,
          reRequestSendTime: r.reRequestTime + 500,
          readTime: 0,
          updateTime: r.reRequestTime + 500,
        })),
        requestTime: r.requestTime,
        requestInterval: r.requestInterval,
        reRequestTime: r.reRequestTime,
        comment: r.comment,
        mailLocale: "ko",
        entryTime: r.requestTime - 1000,
        requestSendTime: r.requestTime + 500,
        reRequestSendTime: r.reRequestTime + 500,
        updateTime: r.reRequestTime + 500,
      };
    });

    // 기본 mock 데이터 - 권한 있는 멤버만
    const picked = getPermittedMembers(syncId).slice(0, 1 + (seed % 3));
    const mock = picked.map((m, i) => {
      const requestId = `${seed.toString(16).padStart(8, "0")}${i}req${m.userId
        .replace(/\W/g, "")
        .slice(0, 6)}`;
      const requestTime = now - day * (3 + i);
      const reRequestTime = requestTime + day;
      const comment = [
        "문서 열람 부탁드립니다.",
        "확인 요청드립니다.",
        "검토 후 의견 주세요.",
        "열람 후 회신 바랍니다.",
      ][i % 4];
      return {
        syncId,
        requestId,
        requestUser: demoUserId,
        targetUserList: [
          {
            syncId,
            requestId,
            requestUser: demoUserId,
            targetUser: m.userId,
            targetUserStatus: 1,
            targetUserType: m.userType,
            targetUserName: m.userName,
            targetUserEmail: m.email,
            requestTime,
            requestInterval: 1,
            reRequestTime,
            comment,
            mailLocale: "ko",
            entryTime: requestTime - 1000,
            requestSendTime: requestTime + 500,
            reRequestSendTime: reRequestTime + 500,
            readTime: i === 0 ? now - day : 0,
            updateTime: reRequestTime + 500,
          },
        ],
        requestTime,
        requestInterval: 1,
        reRequestTime,
        comment,
        mailLocale: "ko",
        entryTime: requestTime - 1000,
        requestSendTime: requestTime + 500,
        reRequestSendTime: reRequestTime + 500,
        updateTime: reRequestTime + 500,
      };
    });

    return HttpResponse.json(
      [...stored, ...mock].filter((r) => !isReadRequestDeleted(r.requestId))
    );
  }),
  // ──────────────────────────────────────────
  // 열람 요청 삭제 (POST /wapi/contents/request/read/{syncId}/{requestId}/delete)
  // ──────────────────────────────────────────
  http.post(
    /\/wapi\/contents\/request\/read\/([^/]+)\/([^/]+)\/delete/,
    async ({ request }) => {
      await delay(100);
      const url = new URL(request.url);
      const parts =
        url.pathname.split("/wapi/contents/request/read/")[1]?.split("/") ?? [];
      const syncId = parts[0] ?? "";
      const requestId = parts[1] ?? "";
      deleteReadRequest(syncId, requestId);
      return HttpResponse.json({
        code: "SUCCESS_TO_DELETE_READ_REQUEST",
        message: "SUCCESS_TO_DELETE_READ_REQUEST",
      });
    }
  ),
  // ──────────────────────────────────────────
  // 열람 요청 전송 (POST /wapi/contents/request/read)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/request/read"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const { syncId, targetUserList, requestInterval, comment, timezoneOffset } =
      body;
    const resolvedRequestTime = body.requestTime || Date.now();
    const resolvedInterval = requestInterval ?? 0;

    // requestInterval이 0이고 requestTime이 0이면 즉시 전송이므로 store에 저장하지 않음
    if (!(resolvedInterval === 0 && body.requestTime === 0)) {
      addReadRequest({
        syncId,
        requestUser: demoUserId,
        targetUserList: targetUserList ?? [],
        requestInterval: resolvedInterval,
        requestTime: resolvedRequestTime,
        comment: comment ?? "",
        timezoneOffset: timezoneOffset ?? -540,
      });
    }
    return HttpResponse.json({
      syncId,
      targetUserList,
      requestTime: resolvedRequestTime,
      requestInterval: resolvedInterval,
      comment,
      timezoneOffset,
    });
  }),

  // ──────────────────────────────────────────
  // 멤버 검색 / 프로필
  // ──────────────────────────────────────────
  http.post(
    rx("/wapi/workgroups/members/searchAll/paging"),
    async ({ request }) => {
      await delay(200);
      const body: any = await request.json().catch(() => ({}));
      let members = [...demoMembers];
      // userType 필터: type="internal"→1, type="external"→2
      if (body.type === "internal") {
        members = members.filter((m) => m.userType === 1);
      } else if (body.type === "external") {
        members = members.filter((m) => m.userType === 2);
      }
      if (body.searchOpt) {
        const q = body.searchOpt.toLowerCase();
        members = members.filter(
          (m) =>
            m.userName?.toLowerCase().includes(q) ||
            m.userId.toLowerCase().includes(q) ||
            m.email?.toLowerCase().includes(q)
        );
      }
      return HttpResponse.json({
        userList: members,
        searchOpt: body.searchOpt ?? "",
        internalPos: body.internalPos ?? -1,
        externalPos: body.externalPos ?? -1,
        pageRange: 1,
        posPage: 1,
        rowRange: 50,
        total: members.length,
      });
    }
  ),

  http.post(rx("/wapi/workgroups/members"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const m =
      demoMembers.find((x) => x.userId === body.userId) ?? demoMembers[0];
    return HttpResponse.json(m);
  }),

  // ──────────────────────────────────────────
  // 조직도 트리 (SelectUserModal - Internal Users)
  // POST /wapi/organs/tree
  // ──────────────────────────────────────────
  http.post(rx("/wapi/organs/tree"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const path: string = body.path ?? "ae4fc0f5fd8f4142834c1ad4e929a514";
    const internalMembers = demoMembers.filter((m) => m.userType === 1);

    // 루트 요청 → 부서 목록 반환
    if (path === "ae4fc0f5fd8f4142834c1ad4e929a514") {
      const depts = [
        ...new Map(
          internalMembers.map((m) => [
            m.deptCode,
            { code: m.deptCode, name: m.deptName },
          ])
        ).values(),
      ];
      return HttpResponse.json({
        path,
        version: "1.0",
        nodes: depts.map((d) => ({
          columnText: d.name,
          subTree: [],
          showChildren: false,
          hasChildren: true,
          value: `dept_${d.code}`,
          type: "dept",
          isPolicySet: false,
        })),
      });
    }

    // 부서 클릭 → 해당 부서 사용자 반환
    const deptCode = path.replace("dept_", "");
    const members = internalMembers.filter((m) => m.deptCode === deptCode);
    return HttpResponse.json({
      path,
      version: "1.0",
      nodes: members.map((m) => ({
        columnText: m.userName,
        subTree: [],
        showChildren: false,
        hasChildren: false,
        value: m.userId,
        type: "user",
        isPolicySet: false,
      })),
    });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 생성
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/insert"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const wg = body.workgroup ?? {};
    const workgroupId = `wg-hub-${Date.now()}`;
    const workgroupName = wg.workgroupName ?? "New Workgroup";
    const managerId = wg.managerId ?? demoUserId;
    const managerMember = demoMembers.find((m) => m.userId === managerId);
    const managerName = managerMember?.userName ?? "Demo User";

    const buildMember = (m: any, wgName: string) => {
      const found = demoMembers.find((dm) => dm.userId === m.userId);
      const base = found ?? m;
      return {
        dsdCode: null,
        workgroupName: wgName,
        userId: base.userId,
        userName: base.userName ?? m.userName ?? "",
        email: (base as any).email ?? "",
        phoneNumber: "",
        companyCode: (base as any).deptCode ?? null,
        companyName: (base as any).deptName ?? null,
        deptCode: (base as any).deptCode ?? null,
        deptName: (base as any).deptName ?? null,
        positionCode: (base as any).positionCode ?? null,
        positionName: (base as any).positionName ?? null,
        roleCode: (base as any).roleCode ?? null,
        roleName: (base as any).roleName ?? null,
        userType: m.userType ?? 1,
        status: m.status ?? 1,
        etc: null,
        linkedYN: null,
        withdrawYN: "N",
        workgroupCount: `${wgName} `,
        lastLoginTime: 0,
        inviteTime: 0,
      };
    };

    const now = Date.now();
    const newWg = {
      dsdCode: null,
      workgroupId,
      workgroupName,
      desc: wg.desc ?? "",
      managerId,
      managerName,
      favorite: "N",
      members: (wg.members ?? []).map((m: any) =>
        buildMember(m, workgroupName)
      ),
      withdrawMembers: null,
      invitePolicy: wg.invitePolicy ?? 0,
      externalUserCount: 0,
      internalUserCount: (wg.members ?? []).length,
      status: 1,
      startDate: wg.startDate ?? -1,
      endDate: wg.endDate ?? -1,
      logDate: now,
      updateDate: now,
      isValidate: 1,
      docSize: 0,
      docCount: 0,
      readRequestCount: 0,
      webMeetingCount: 0,
      chatCount: 0,
      parentWorkgroupId: wg.parentWorkgroupId ?? null,
      rootWorkgroupId: wg.parentWorkgroupId ?? workgroupId,
      hasSubWorkgroup: null,
      workgroupJoined: true,
      workgroupType: wg.createWorkgroupHub
        ? "HUB"
        : wg.workgroupType ?? "GENERAL",
    };
    addWorkgroup(newWg as any);
    return HttpResponse.json(newWg);
  }),

  http.post(rx("/wapi/workgroups/insert/private"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const wg = body.workgroup ?? {};
    const workgroupId = `wg-private-${Date.now()}`;
    const parentWorkgroupId = wg.parentWorkgroupId ?? null;
    const managerId = demoUserId;

    // private 워크그룹명: {parentName}_{targetUserName}({targetUserId})
    const parentWg = store_workgroups.find(
      (w) => w.workgroupId === parentWorkgroupId
    );
    const parentName =
      parentWg?.workgroupName ?? wg.workgroupName ?? "Workgroup";
    const targetMember = (wg.members ?? []).find(
      (m: any) => m.userId !== managerId
    );
    const targetUserName = targetMember?.userName;
    const workgroupName = targetMember
      ? targetUserName
        ? `${parentName}_${targetUserName}(${targetMember.userId})`
        : `${parentName}_${targetMember.userId}`
      : parentName;

    const buildMember = (m: any, wgName: string) => {
      const found = demoMembers.find((dm) => dm.userId === m.userId);
      const base = found ?? m;
      return {
        dsdCode: null,
        workgroupName: wgName,
        userId: base.userId,
        userName: base.userName ?? m.userName ?? "",
        email: (base as any).email ?? "",
        phoneNumber: "",
        companyCode: (base as any).deptCode ?? null,
        companyName: (base as any).deptName ?? null,
        deptCode: (base as any).deptCode ?? null,
        deptName: (base as any).deptName ?? null,
        positionCode: (base as any).positionCode ?? null,
        positionName: (base as any).positionName ?? null,
        roleCode: (base as any).roleCode ?? null,
        roleName: (base as any).roleName ?? null,
        userType: m.userType ?? 1,
        status: m.status ?? 1,
        etc: null,
        linkedYN: null,
        withdrawYN: "N",
        workgroupCount: `${wgName} `,
        lastLoginTime: 0,
        inviteTime: 0,
      };
    };

    const now = Date.now();
    const newWg = {
      dsdCode: null,
      workgroupId,
      workgroupName,
      desc: wg.desc ?? "",
      managerId,
      managerName:
        demoMembers.find((m) => m.userId === managerId)?.userName ?? managerId,
      favorite: "N",
      members: (wg.members ?? []).map((m: any) =>
        buildMember(m, workgroupName)
      ),
      withdrawMembers: null,
      invitePolicy: wg.invitePolicy ?? 0,
      externalUserCount: 0,
      internalUserCount: (wg.members ?? []).length,
      status: 1,
      startDate: wg.startDate ?? -1,
      endDate: wg.endDate ?? -1,
      logDate: now,
      updateDate: now,
      isValidate: 1,
      docSize: 0,
      docCount: 0,
      readRequestCount: 0,
      webMeetingCount: 0,
      chatCount: 0,
      parentWorkgroupId,
      rootWorkgroupId: parentWorkgroupId ?? workgroupId,
      hasSubWorkgroup: null,
      workgroupJoined: true,
      workgroupType: "PRIVATE",
    };
    addWorkgroup(newWg as any);
    return HttpResponse.json(newWg);
  }),

  // ──────────────────────────────────────────
  // 워크그룹 정보 수정 (멤버 추가/삭제 포함)
  // POST /wapi/workgroups/update
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/update"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const wgId: string = body.workgroupId ?? body.workgroup?.workgroupId ?? "";
    const wg = store_workgroups.find((w) => w.workgroupId === wgId);
    if (wg && body.workgroup) {
      const incoming = body.workgroup;
      // 이름 변경
      if (incoming.workgroupName) {
        renameWorkgroup(wgId, incoming.workgroupName);
      }
      // 멤버 변경: changeCode가 DELETE면 제거, 아니면 추가
      const newlyAddedMembers: any[] = [];
      if (Array.isArray(incoming.members)) {
        incoming.members.forEach((m: any) => {
          if (m.changeCode === "DELETE") {
            wg.members = (wg.members ?? []).filter(
              (x: any) => x.userId !== m.userId
            );
          } else if (
            m.changeCode === "CREATE" ||
            m.changeCode === "INSERT" ||
            m.changeCode === "ADD"
          ) {
            const exists = (wg.members ?? []).some(
              (x: any) => x.userId === m.userId
            );
            if (!exists) {
              (wg.members as any[]).push(m);
              newlyAddedMembers.push(m);
            }
          }
        });
      }

      // Hub 워크그룹에 멤버 추가 시 PRIVATE 서브 워크그룹 생성
      const isHub =
        wg.workgroupType === "HUB" || incoming.workgroupType === "HUB";
      if (isHub) {
        const now = Date.now();
        const hubName = wg.workgroupName ?? wgId;
        newlyAddedMembers.forEach((m: any) => {
          if (m.userId === wg.managerId) return;
          const found = demoMembers.find((dm) => dm.userId === m.userId);
          const base = found ?? m;
          const userName = base.userName ?? m.userName;
          const privateWgName = userName
            ? `${hubName}_${userName}(${m.userId})`
            : `${hubName}_${m.userId}`;
          addWorkgroup({
            dsdCode: null,
            workgroupId: `wg-private-${Date.now()}-${m.userId}`,
            workgroupName: privateWgName,
            desc: "",
            managerId: wg.managerId,
            managerName: wg.managerName,
            favorite: "N",
            members: [
              { ...wg.members?.find((x: any) => x.userId === wg.managerId) },
              {
                ...base,
                workgroupName: privateWgName,
                userType: m.userType ?? 1,
                status: m.status ?? 1,
                withdrawYN: "N",
              },
            ],
            withdrawMembers: null,
            invitePolicy: 0,
            externalUserCount: 0,
            internalUserCount: 2,
            status: 1,
            startDate: -1,
            endDate: -1,
            logDate: now,
            updateDate: now,
            isValidate: 1,
            docSize: 0,
            docCount: 0,
            readRequestCount: 0,
            webMeetingCount: 0,
            chatCount: 0,
            parentWorkgroupId: wgId,
            rootWorkgroupId: wgId,
            hasSubWorkgroup: null,
            workgroupJoined: true,
            workgroupType: "PRIVATE",
          } as any);
        });
      }
    }
    return HttpResponse.json({ code: "200", message: "OK" });
  }),

  // ──────────────────────────────────────────
  // 멤버 강제 삭제 (관리자용)
  // POST /wapi/workgroups/members/delete/user
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/members/delete/user"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const { userId, workgroupId } = body;
    if (workgroupId) {
      const wg = store_workgroups.find((w) => w.workgroupId === workgroupId);
      if (wg) {
        wg.members = (wg.members ?? []).filter((m: any) => m.userId !== userId);
      }
    }
    return HttpResponse.json({ code: "200", message: "OK" });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 멤버 정보 업데이트 (권한 변경 등)
  // POST /wapi/workgroups/members/update
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/members/update"), async () => {
    await delay(50);
    return HttpResponse.json({ code: "200", message: "OK" });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 삭제 (update/status) — Workgroup[] 반환 필수
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/update/status"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const { workgroupId, status } = body;
    if (status === 0) {
      deleteWorkgroup(workgroupId);
    }
    return HttpResponse.json(store_workgroups);
  }),

  // ──────────────────────────────────────────
  // 워크그룹 멤버 자진 탈퇴 (withdraw)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/members/withdraw"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const workgroupId: string = body.workgroupId ?? "";
    const users: any[] = body.users ?? [];
    const isSelfWithdraw = users.some((u) => u.userId === demoUserId);
    if (isSelfWithdraw && workgroupId) {
      deleteWorkgroup(workgroupId);
    }
    return HttpResponse.json({ code: "200", message: "OK" });
  }),

  // ──────────────────────────────────────────
  // 파일 썸네일 — mockData/documents.ts의 thumbnails 배열 사용
  // GET /wapi/contents/thumbnail/{syncId}/{revisionNo}
  // ──────────────────────────────────────────
  http.get(/\/wapi\/contents\/thumbnail\/([^/]+)\/([^/]+)/, ({ params }) => {
    const syncId = (Object.values(params)[0] as string) ?? "";
    // syncId 해시로 thumbnails 배열 인덱스 결정 → 항상 같은 이미지
    let hash = 0;
    for (let i = 0; i < syncId.length; i++)
      hash = (hash * 31 + syncId.charCodeAt(i)) >>> 0;
    const index = hash % thumbnails.length;
    return new Response(thumbnails[index]);
  }),

  // ──────────────────────────────────────────
  // 화상회의 즐겨찾기
  // POST /wapi/zoom/favorite
  // ──────────────────────────────────────────
  http.post(rx("/wapi/zoom/favorite"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    // meetingId는 meetingId + occurrenceId 조합으로 전달됨
    // store_meetings의 실제 meetingId와 매칭하기 위해 store를 순회
    const matchedMeeting = store_meetings.find(
      (m) =>
        m.meetingId + (m.occurrenceId ?? "") === body.meetingId ||
        m.meetingId === body.meetingId
    );
    if (matchedMeeting) {
      toggleMeetingFavorite(matchedMeeting.meetingId, body.flag ? "Y" : "N");
    }
    // reducer가 payload.meetingId, payload.flag를 사용하므로 request body 그대로 반환
    return HttpResponse.json({ ...body });
  }),

  // ──────────────────────────────────────────
  // 즐겨찾기된 미팅 목록
  // POST /wapi/workgroups/favorite/meeting/list
  // ──────────────────────────────────────────
  http.post(rx("/wapi/workgroups/favorite/meeting/list"), async () => {
    await delay(50);
    const favMeetings = store_meetings.filter((m) => m.favorite === "Y");
    return HttpResponse.json({
      hasNext: false,
      meetingInfoList: favMeetings,
      nextStartTime: null,
      pageNo: 0,
      requestResultSize: favMeetings.length,
      total: favMeetings.length,
    });
  }),

  // ──────────────────────────────────────────
  // Zoom 연동 URL — 팝업이 즉시 닫히도록 auto-close 페이지 반환
  // GET /wapi/zoom/registUrl
  // ──────────────────────────────────────────
  http.get(rx("/wapi/zoom/registUrl"), async () => {
    await delay(300);
    return HttpResponse.json({
      entity: {
        registUrl: `${window.location.origin}/demo-zoom-callback.html`,
      },
    });
  }),

  // ──────────────────────────────────────────
  // Zoom 연결 상태 (myInfo) — appRegisterStatus.zoom = true 처리
  // ──────────────────────────────────────────
  http.get(rx("/wapi/zoom/myInfo"), async () => {
    await delay(100);
    return HttpResponse.json({ userId: demoUserId, connected: true });
  }),

  // ──────────────────────────────────────────
  // 미팅 상세 (MeetingDetailDto)
  // GET /wapi/zoom/{workgroupId}/meetings/{meetingId}/detail
  // ──────────────────────────────────────────
  http.get(
    /\/wapi\/zoom\/([^/]+)\/meetings\/([^/]+)\/detail/,
    async ({ params }) => {
      await delay(50);
      const parts = Object.values(params) as string[];
      const meetingId = parts[1];
      const meeting =
        store_meetings.find((m) => m.meetingId === meetingId) ??
        store_meetings[0];
      const startMs = new Date(meeting.startTime).getTime();
      return HttpResponse.json({
        meetingBasicInfo: meeting,
        participantList: meeting.participantList ?? [],
        zoomMeetingDetailDTO: {
          uuid: `uuid-${meeting.meetingId}`,
          id: parseInt(meeting.meetingId.replace(/\D/g, ""), 10) || 1001,
          host_id: meeting.hostId,
          topic: meeting.topic,
          type: meeting.type,
          status: meeting.status,
          start_time: meeting.startTime,
          duration: meeting.duration,
          timezone: meeting.timezone,
          created_at: new Date(startMs - 86400000).toISOString(),
          agenda: "",
          start_url: "",
          join_url: meeting.joinUrl ?? "",
          password: meeting.password ?? "",
          h323_password: "",
          pstn_password: "",
          encrypted_password: "",
          pmi: 0,
          tracking_fields: "",
          occurrences: "",
          recurrence: null,
          settings: {
            host_video: true,
            participant_video: true,
            cn_meeting: false,
            in_meeting: false,
            join_before_host: false,
            mute_upon_entry: false,
            watermark: false,
            use_pmi: false,
            approval_type: 0,
            audio: "both",
            auto_recording: "none",
            enforce_login: false,
            enforce_login_domains: "",
            alternative_hosts: "",
            close_registration: false,
            waiting_room: false,
            request_permission_to_unmute_participants: false,
            registrants_email_notification: false,
          },
        },
      });
    }
  ),

  // ──────────────────────────────────────────
  // 워크그룹별 미팅 목록 (paging)
  // POST /wapi/zoom/{workgroupId}/meetings/paging
  // ──────────────────────────────────────────
  http.post(
    /\/wapi\/zoom\/([^/]+)\/meetings\/paging/,
    async ({ params, request }) => {
      await delay(50);
      const workgroupId = (Object.values(params)[0] as string) ?? "";
      const body: any = await request.json().catch(() => ({}));
      let list = store_meetings.filter((m) => m.workgroupId === workgroupId);
      if (body.timeDirection === "past") {
        list = [];
      }
      return HttpResponse.json({
        hasNext: false,
        meetingInfoList: list,
        nextStartTime: null,
        pageNo: 0,
        requestResultSize: list.length,
        total: list.length,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 화상회의 생성
  // POST /wapi/zoom/{workgroupId}/meetings
  // ──────────────────────────────────────────
  http.post(
    /\/wapi\/zoom\/([^/]+)\/meetings(?:\/)?$/,
    async ({ params, request }) => {
      await delay(200);
      const workgroupId = (Object.values(params)[0] as string) ?? "";
      const body: any = await request.json().catch(() => ({}));
      const wg = getWorkgroupMap()[workgroupId];
      const dto = body.zoomCreateOrUpdateMeetingRequestDTO ?? {};
      const participantIds: string[] = body.participantList ?? [];

      const baseId = Date.now();
      const meetingId = `mtg-${baseId}`;
      const numericId = baseId % 100000000000;
      const startMs = dto.start_time
        ? new Date(dto.start_time).getTime()
        : Date.now() + 86400000;
      const duration = parseInt(dto.duration ?? "60", 10);
      const password = dto.password ?? "demo1234";
      const meetingType = parseInt(dto.type ?? "2", 10);
      const recurrence = dto.recurrence ?? null;

      const participants = participantIds.map(
        (uid: string) =>
          demoMembers.find((m) => m.userId === uid) ?? {
            userId: uid,
            userName: uid,
            userType: 1,
          }
      );

      const makeOccurrenceTimes = (): number[] => {
        if (meetingType !== 8 || !recurrence) return [startMs];
        const endTimes: number | null = recurrence.end_times ?? null; // 횟수 제한
        const endMs = recurrence.end_date_time
          ? new Date(recurrence.end_date_time).getTime()
          : startMs + 365 * 86400000;
        const times: number[] = [];
        const rType: number = recurrence.type ?? 1;
        const withinLimit = () => endTimes === null || times.length < endTimes;

        if (rType === 1) {
          // Daily: repeat every repeat_interval days
          const intervalMs = (recurrence.repeat_interval ?? 1) * 86400000;
          let cur = startMs;
          while (cur <= endMs && withinLimit()) {
            times.push(cur);
            cur += intervalMs;
          }
        } else if (rType === 2) {
          // Weekly: weekly_days = comma-separated (1=Mon,...,6=Sat,7=Sun)
          const intervalWeeks = recurrence.repeat_interval ?? 1;
          const dayNums: number[] = String(recurrence.weekly_days ?? "1")
            .split(",")
            .map((d: string) => {
              const n = parseInt(d.trim(), 10);
              return n === 7 ? 0 : n; // 7=Sun → JS getDay 0
            });
          const startDate = new Date(startMs);
          const startDow = startDate.getDay();
          const mondayOffset = startDow === 0 ? -6 : 1 - startDow;
          const weekMonday = new Date(startMs + mondayOffset * 86400000);
          weekMonday.setHours(
            startDate.getHours(),
            startDate.getMinutes(),
            startDate.getSeconds(),
            0
          );

          let weekStart = weekMonday.getTime();
          while (weekStart <= endMs + 6 * 86400000 && withinLimit()) {
            dayNums.forEach((dow) => {
              if (!withinLimit()) return;
              const offset = dow === 0 ? 6 : dow - 1;
              const t = weekStart + offset * 86400000;
              if (t >= startMs && t <= endMs) times.push(t);
            });
            weekStart += intervalWeeks * 7 * 86400000;
          }
        } else if (rType === 3) {
          // Monthly: monthly_day = day of month (e.g. 10 → every 10th)
          const intervalMonths = recurrence.repeat_interval ?? 1;
          const targetDay: number | null = recurrence.monthly_day ?? null;
          const startDate = new Date(startMs);
          let cur = new Date(startMs);
          if (targetDay !== null) cur.setDate(targetDay);
          // If targetDay is before startDate in first month, move to next month
          if (cur.getTime() < startMs) {
            cur.setMonth(cur.getMonth() + intervalMonths);
            if (targetDay !== null) cur.setDate(targetDay);
          }
          while (cur.getTime() <= endMs && withinLimit()) {
            times.push(cur.getTime());
            cur = new Date(cur);
            cur.setMonth(cur.getMonth() + intervalMonths);
            if (targetDay !== null) cur.setDate(targetDay);
          }
        }

        return times.sort((a, b) => a - b);
      };

      const occurrenceTimes = makeOccurrenceTimes();

      const occurrences = occurrenceTimes.map((t, i) => ({
        occurrence_id: String(t),
        start_time: new Date(t).toISOString(),
        duration,
        status: "available",
      }));

      // store에는 각 occurrence를 별도 미팅으로 저장 (type 8이면 occurrenceId 설정)
      occurrenceTimes.forEach((t, i) => {
        const occId = meetingType === 8 ? String(t) : "NONE";
        const mId = meetingType === 8 ? `${meetingId}-occ-${i}` : meetingId;
        const newMeeting: any = {
          meetingId: mId,
          workgroupId,
          workgroupName: wg?.workgroupName ?? "",
          occurrenceId: occId,
          syncId: null,
          docName: null,
          type: meetingType,
          topic: dto.topic ?? "",
          ownerId: demoUserId,
          startTime: new Date(t).toISOString(),
          endTime: t + duration * 60000,
          createTime: Date.now(),
          updateTime: Date.now(),
          timezone: dto.timezone ?? "Asia/Seoul",
          startUrl: "FAKEURL",
          joinUrl: "FAKEURL",
          password,
          duration,
          openStatus: body.openStatus ?? "PUBLIC",
          hostId: demoUserId,
          status: "waiting",
          favorite: "N",
          participantCnt: participants.length,
          participantListStr: participantIds.join(","),
          participantList: participants,
          timezoneOffset: 540,
        };
        addMeeting(newMeeting);
      });

      const firstMeeting = {
        meetingId,
        workgroupId,
        topic: dto.topic ?? "",
        startTime: new Date(startMs).toISOString(),
      };

      return HttpResponse.json({
        "200": true,
        status: "OK",
        error: null,
        entity: {
          uuid: `${meetingId}-uuid`,
          id: numericId,
          host_id: demoUserId,
          topic: dto.topic ?? "",
          type: meetingType,
          status: "waiting",
          start_time: new Date(startMs).toISOString(),
          duration,
          timezone: dto.timezone ?? "Asia/Seoul",
          created_at: new Date().toISOString(),
          agenda: dto.agenda ?? "",
          start_url: `https://us04web.zoom.us/s/${numericId}?zak=demo`,
          join_url: `https://us04web.zoom.us/j/${numericId}?pwd=demo`,
          password,
          h323_password: "",
          pstn_password: "",
          encrypted_password: password,
          pmi: 0,
          tracking_fields: null,
          occurrences: meetingType === 8 ? occurrences : null,
          settings: dto.settings ?? {},
          recurrence,
        },
        meetingInfo: firstMeeting,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 화상회의 수정
  // PUT /wapi/zoom/{workgroupId}/meetings/{meetingId}
  // ──────────────────────────────────────────
  http.put(
    /\/wapi\/zoom\/([^/]+)\/meetings\/([^/]+)(?:\/)?$/,
    async ({ params, request }) => {
      await delay(200);
      const parts = Object.values(params) as string[];
      const meetingId = parts[1];
      const body: any = await request.json().catch(() => ({}));
      const dto = body.zoomCreateOrUpdateMeetingRequestDTO ?? {};
      const participantIds: string[] = body.participantList ?? [];

      const startMs = dto.start_time
        ? new Date(dto.start_time).getTime()
        : undefined;
      const duration = dto.duration ? parseInt(dto.duration, 10) : undefined;

      const participants =
        participantIds.length > 0
          ? participantIds.map(
              (uid: string) =>
                demoMembers.find((m) => m.userId === uid) ?? {
                  userId: uid,
                  userName: uid,
                  userType: 1,
                }
            )
          : undefined;

      const patch: any = {
        updateTime: Date.now(),
        ...(dto.topic && { topic: dto.topic }),
        ...(dto.start_time && { startTime: dto.start_time }),
        ...(startMs && duration && { endTime: startMs + duration * 60000 }),
        ...(duration !== undefined && { duration }),
        ...(dto.timezone && { timezone: dto.timezone }),
        ...(dto.password && { password: dto.password }),
        ...(body.openStatus && { openStatus: body.openStatus }),
        ...(participants && {
          participantList: participants,
          participantCnt: participants.length,
          participantListStr: participantIds.join(","),
        }),
      };

      const updated = updateMeeting(meetingId, patch);

      return HttpResponse.json({
        "200": true,
        status: "OK",
        error: null,
        entity: updated
          ? {
              uuid: `${meetingId}-uuid`,
              id: meetingId,
              topic: updated.topic,
              start_time: updated.startTime,
              duration: updated.duration,
              timezone: updated.timezone,
              status: updated.status,
            }
          : null,
        meetingInfo: updated,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 화상회의 삭제
  // DELETE /wapi/zoom/{workgroupId}/meetings/{meetingId}
  // ──────────────────────────────────────────
  http.post(
    /\/wapi\/zoom\/([^/]+)\/meetings\/([^/]+)\/delete/,
    async ({ params }) => {
      await delay(50);
      const parts = Object.values(params) as string[];
      const meetingId = parts[1];
      deleteMeeting(meetingId);
      return HttpResponse.json({ "200": true, status: "OK", error: null });
    }
  ),

  // ──────────────────────────────────────────
  // 미팅 목록 (키워드 검색용)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/zoom/meetings/list"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const workgroupIds: string[] = body.workgroupIds ?? [];
    let list = store_meetings;
    if (workgroupIds.length > 0) {
      list = store_meetings.filter((m) => workgroupIds.includes(m.workgroupId));
    }
    if (body.searchKeyword) {
      const q = body.searchKeyword.toLowerCase();
      list = list.filter((m) => m.topic.toLowerCase().includes(q));
    }
    return HttpResponse.json({
      hasNext: false,
      meetingInfoList: list,
      nextStartTime: null,
      pageNo: 0,
      requestResultSize: list.length,
      total: list.length,
    });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 트리
  // ──────────────────────────────────────────
  http.get(rx("/wapi/workgroups/tree"), async () => {
    await delay(50);
    return HttpResponse.json(store_workgroups);
  }),

  // ──────────────────────────────────────────
  // 워크그룹 채팅 최근 GET /wapi/chat/recent/:workgroupId
  // ──────────────────────────────────────────
  http.get(/\/wapi\/chat\/recent\/([^/?]+)$/, async ({ request }) => {
    await delay(80);
    // /wapi/chat/recent/list 는 아래 핸들러에서 처리
    if (request.url.includes("/wapi/chat/recent/list")) return;
    return HttpResponse.json({ unreadCount: 0, chatCount: 0, chats: [] });
  }),

  // ──────────────────────────────────────────
  // 채팅 액세스 토큰 GET /wapi/chat/accesToken
  // ──────────────────────────────────────────
  http.get(/\/wapi\/chat\/accesToken/, async () => {
    await delay(80);
    return HttpResponse.text("demo-chat-token");
  }),

  // ──────────────────────────────────────────
  // 채팅 최근 목록 GET /wapi/chat/recent/list
  // ──────────────────────────────────────────
  http.get(/\/wapi\/chat\/recent\/list/, async () => {
    await delay(100);
    const now = Date.now();
    const day = 86400000;

    const makeChatMember = (m: (typeof demoMembers)[0], wgName: string) => ({
      dsdCode: null,
      workgroupName: wgName,
      userId: m.userId,
      userName: m.userName,
      email: m.email,
      phoneNumber: "",
      companyCode: m.userType === 1 ? "demo-company-code" : null,
      companyName: m.userType === 1 ? "Wrapsody eCo" : null,
      deptCode: m.deptCode,
      deptName: m.deptName,
      positionCode: m.positionCode ?? null,
      positionName: m.positionName ?? null,
      roleCode: m.roleCode ?? null,
      roleName: m.roleName ?? null,
      userType: m.userType,
      status: m.status,
      etc: null,
      linkedYN: m.linkedYN ?? null,
      withdrawYN: "N",
      workgroupCount: `${wgName} `,
      lastLoginTime: 0,
      inviteTime: 0,
    });

    const makeConvo = (
      idx: number,
      workgroupId: string,
      name: string,
      memberIds: string[],
      latestMessage: string,
      daysAgo: number
    ) => {
      const members = demoMembers.filter((m) => memberIds.includes(m.userId));
      const convoId = `demo-chat-${idx.toString().padStart(4, "0")}`;
      const latestMessageAt =
        now - daysAgo * day + Math.floor(Math.random() * day);
      return {
        conversation: {
          createdAt: now - (daysAgo + 10) * day,
          updatedAt: latestMessageAt,
          convoId,
          convoType: 1,
          roomType: 5,
          name,
          relationId: workgroupId,
          readAt: 0,
          unread: Math.floor(Math.random() * 5),
          memberCount: members.length,
          notificationType: 1,
          latestMessage,
          latestMessageAt,
        },
        workgroupMembers: members.map((m) => makeChatMember(m, name)),
      };
    };

    return HttpResponse.json([
      makeConvo(
        1,
        "wg-product-design",
        "Product Design",
        [demoUserId, "alice.johnson", "emma.wilson", "kate.davis"],
        "Design system component update is complete.",
        0
      ),
      makeConvo(
        2,
        "wg-engineering-core",
        "Engineering Core",
        [demoUserId, "alice.johnson", "bob.chen", "james.brown", "david.kim"],
        "Sharing the API spec — please review when you get a chance.",
        1
      ),
      makeConvo(
        3,
        "wg-executive-projects",
        "Executive Projects",
        [demoUserId, "alice.johnson", "grace.lee", "iris.taylor"],
        "Tomorrow's meeting has been moved to 3 PM.",
        2
      ),
      makeConvo(
        4,
        "wg-marketing-campaign",
        "Marketing Campaign",
        [demoUserId, "carol.smith", "kate.davis"],
        "Please review the campaign materials at your earliest convenience.",
        3
      ),
      makeConvo(
        5,
        "wg-security-compliance",
        "Security & Compliance",
        [demoUserId, "david.kim", "iris.taylor", "alice.johnson"],
        "Sharing the security audit schedule for your reference.",
        5
      ),
    ]);
  }),

  // ──────────────────────────────────────────
  // 워크그룹 알림 설정
  // ──────────────────────────────────────────
  http.get(/\/wapi\/policy\/notification\//, async () => {
    await delay(100);
    return HttpResponse.json({
      usingWorkgroupInvitedAlert: true,
      usingFileAlert: true,
      fileAlert: ["fileshare", "revision"],
      usingReadRequestAlert: true,
      readRequestAlert: ["readRequest", "readRequestConfirm"],
    });
  }),

  // ──────────────────────────────────────────
  // 워크그룹 알림 설정 업데이트
  // ──────────────────────────────────────────
  http.post(/\/wapi\/policy\/notification\//, async ({ request }) => {
    await delay(100);
    const body = await request.json().catch(() => ({}));
    return HttpResponse.json(body);
  }),

  // ──────────────────────────────────────────
  // papi - 액션 URL 정보
  // POST /papi/actionUrl/:uid/info
  // GET  /papi/actionUrl/syncId/:syncId/info
  // ──────────────────────────────────────────
  http.post(/\/papi\/actionUrl\/([^/]+)\/info/, async ({ params }) => {
    await delay(100);
    const uid = (Object.values(params)[0] as string) ?? "";
    const syncId = uid.startsWith("demo-") ? uid.slice(5) : uid;
    const doc = findDoc(syncId) ?? store_docs[0];
    const policy = getLinkPolicy(doc.syncId);
    return HttpResponse.json(buildActionUrlInfo(uid, doc, policy));
  }),
  http.get(/\/papi\/actionUrl\/syncId\/([^/]+)\/info/, async ({ params }) => {
    await delay(100);
    const syncId = (Object.values(params)[0] as string) ?? "";
    const doc = findDoc(syncId) ?? store_docs[0];
    const policy = getLinkPolicy(doc.syncId);
    return HttpResponse.json(
      buildActionUrlInfo(`demo-${doc.syncId}`, doc, policy)
    );
  }),

  // ──────────────────────────────────────────
  // papi - 공개 파일 상세 (간단)
  // GET /papi/file/detail/{syncId}
  // ──────────────────────────────────────────
  http.get(/\/papi\/file\/detail\/([^/]+)/, async ({ params }) => {
    await delay(100);
    const syncId = (Object.values(params)[0] as string) ?? "";
    const doc = findDoc(syncId) ?? store_docs[0];
    return HttpResponse.json({
      contentName: doc.contentName,
      fileSize: doc.fileSize,
      master: {
        userId: doc.checkinUser.userId,
        userName: doc.checkinUser.userName,
      },
    });
  }),

  // ──────────────────────────────────────────
  // 워크스페이스 목록 (BaseLayout 마운트 시 호출)
  // POST /wapi/tenancy/list/my
  // ──────────────────────────────────────────
  http.post(rx("/wapi/tenancy/list/my"), async () => {
    await delay(100);
    return HttpResponse.json([
      {
        dsdCode: "DEMO_WORKSPACE",
        alias: "demo",
        myDefaultTenancy: true,
        paymentCode: "FREE",
        tenancyStatus: "RUNNING",
        createUserId: demoUserId,
        masterUserId: demoUserId,
        createdTime: Date.now() - 86400000 * 365,
        updatedTime: Date.now() - 86400000 * 7,
        tenancyUsageSize: 1073741824,
        tenancyName: "Wrapsody eCo Demo",
        currentInternalUserCount: 16,
        currentExternalUserCount: 1,
        maxInternalUserCount: 100,
        maxExternalUserCount: 10,
      },
    ]);
  }),

  // GET /wapi/tenancy/info/my
  http.get(rx("/wapi/tenancy/info/my"), async () => {
    await delay(100);
    return HttpResponse.json({
      userId: demoUserId,
      userName: "Demo User",
      email: "demo.user@wrapsodyeco.com",
      deptCode: "DEPT_001",
      deptName: "Product Team",
      positionCode: "P001",
      positionName: "Manager",
      userType: 1,
      status: 1,
    });
  }),

  // ──────────────────────────────────────────
  // 로그 (WorkgroupLog 페이지) - paging & summary 공통 데이터
  // ──────────────────────────────────────────
  http.post(rx("/wapi/logs/new/paging"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const workgroupId: string = body.workgroupId ?? "";
    const wg = getWorkgroupMap()[workgroupId] ?? store_workgroups[0];
    const allResults = buildWorkgroupLogs(wg);
    const startDate: number | undefined = body.startDate
      ? parseInt(body.startDate)
      : undefined;
    const endDate: number | undefined = body.endDate
      ? parseInt(body.endDate)
      : undefined;
    const filterDocument: number[] = body.filterDocument ?? [];
    const filterWebMeeting: number[] = body.filterWebMeeting ?? [];
    const filterWorkgroup: number[] = body.filterWorkgroup ?? [];
    const filterChat: number[] = body.filterChat ?? [];
    const hasTypeFilter =
      filterDocument.length > 0 ||
      filterWebMeeting.length > 0 ||
      filterWorkgroup.length > 0 ||
      filterChat.length > 0;
    const logResults = allResults.filter((item: any) => {
      if (startDate && item.logDate < startDate) return false;
      if (endDate && item.logDate > endDate) return false;
      if (hasTypeFilter) {
        const lt = item.logType;
        const st: number = item.saveType;
        if (lt === "document") return filterDocument.includes(st);
        if (lt === "webMeeting") return filterWebMeeting.includes(st);
        if (lt === "workgroup" || lt === "workgroupRole")
          return filterWorkgroup.includes(st);
        if (lt === "chat") return filterChat.includes(st);
        return false;
      }
      return true;
    });
    return HttpResponse.json({
      logResults,
      total: logResults.length,
      logId: null,
      pageTotal: 1,
      posPage: 0,
      pageRange: 20,
      rowRange: 20,
    });
  }),

  http.post(rx("/wapi/logs/summary"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const workgroupId: string = body.workgroupId ?? "";
    const wg = getWorkgroupMap()[workgroupId] ?? store_workgroups[0];
    const allItems = buildWorkgroupLogs(wg);
    const startDate: number | undefined = body.startDate
      ? parseInt(body.startDate)
      : undefined;
    const endDate: number | undefined = body.endDate
      ? parseInt(body.endDate)
      : undefined;
    const filterDocument: number[] = body.filterDocument ?? [];
    const filterWebMeeting: number[] = body.filterWebMeeting ?? [];
    const filterWorkgroup: number[] = body.filterWorkgroup ?? [];
    const filterChat: number[] = body.filterChat ?? [];
    const hasTypeFilter =
      filterDocument.length > 0 ||
      filterWebMeeting.length > 0 ||
      filterWorkgroup.length > 0 ||
      filterChat.length > 0;
    const summaryItems = allItems.filter((item: any) => {
      if (startDate && item.logDate < startDate) return false;
      if (endDate && item.logDate > endDate) return false;
      if (hasTypeFilter) {
        const lt = item.logType;
        const st: number = item.saveType;
        if (lt === "document") return filterDocument.includes(st);
        if (lt === "webMeeting") return filterWebMeeting.includes(st);
        if (lt === "workgroup" || lt === "workgroupRole")
          return filterWorkgroup.includes(st);
        if (lt === "chat") return filterChat.includes(st);
        return false;
      }
      return true;
    });
    return HttpResponse.json(summaryItems);
  }),

  http.post(rx("/wapi/logs/detail"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const logIdList: string[] = body.logIdList ?? [];
    const docs = getAllActiveDocs();
    const logResults = logIdList.map((logId) => {
      // 캐시에서 원본 log item 반환
      const cached = logItemCache.get(logId);
      if (cached) return cached;

      // fallback: 내 파일 이력용 (demo-me-log-N 형태)
      const idx = parseInt(logId.replace("demo-me-log-", ""), 10);
      const doc = docs[isNaN(idx) ? 0 : idx] ?? docs[0];
      const member = demoMembers[(isNaN(idx) ? 0 : idx) % demoMembers.length];
      return {
        logId,
        syncId: doc?.syncId ?? "",
        documnetName: doc?.contentName ?? "",
        workgroupId: (doc as any)?.workgroup?.workgroupId ?? "",
        workgroupName: (doc as any)?.workgroup?.workgroupName ?? "",
        userId: member?.userId ?? demoUserId,
        userName: member?.userName ?? "Demo User",
        logType: "document",
        logTypeDbValue: "document",
        historyType: 1,
        saveType: isNaN(idx) ? 7 : idx % 12,
        actionType: "view",
        actionTypeDbValue: "view",
        logDate: Date.now() - ((isNaN(idx) ? 0 : idx) + 1) * 86400000,
        revisionNo: doc?.revisionNo ?? 1,
        logDatas: [],
        logData: null,
        policyLogs: null,
        children: [],
      };
    });
    return HttpResponse.json({
      logResults,
      total: logResults.length,
      logId: "",
    });
  }),

  http.post(rx("/wapi/logs/new/paging/contents/me"), async ({ request }) => {
    await delay(200);
    const body: any = await request.json().catch(() => ({}));
    const now = Date.now();
    const day = 86400000;

    // filterDocument integer → actionType string mapping
    const filterDocumentActionTypeMap: Record<number, string> = {
      0: "share",
      1: "revision",
      2: "sign",
      3: "rename",
      4: "delete",
      5: "download",
      6: "unwrap",
      7: "view",
      8: "masterChange",
      9: "policyChange",
      10: "linkSetting",
      11: "restore",
      12: "view",
    };

    let logResults = getAllActiveDocs().map((doc, i) => {
      const member = demoMembers[i % demoMembers.length];
      const saveType = i % 12;
      const actionType = filterDocumentActionTypeMap[saveType] ?? "view";
      return {
        logId: `demo-me-log-${i}`,
        userId: member.userId,
        userName: member.userName,
        workgroupId: (doc as any).workgroup?.workgroupId ?? "",
        workgroupName: (doc as any).workgroup?.workgroupName ?? "",
        logType: "document",
        logTypeDbValue: "document",
        actionType,
        actionTypeDbValue: actionType,
        logDatas: [],
        logData: null,
        logDate: now - (i + 1) * day,
        entryDate: now - (i + 1) * day,
        syncId: doc.syncId,
        documnetName: doc.contentName,
        result: null,
        status: null,
        saveType,
        historyType: 1,
        revisionNo: doc.revisionNo,
        desc: null,
        ip: "192.111.222.333",
        agentType: 1,
        agentVersion: "3.0",
        policyLogs: null,
        children: [],
      };
    });

    // userId 필터
    if (body.userId) {
      logResults = logResults.filter((l) => l.userId === body.userId);
    }
    // documentName 필터
    if (body.documentName) {
      const q = body.documentName.toLowerCase();
      logResults = logResults.filter((l) =>
        l.documnetName.toLowerCase().includes(q)
      );
    }
    // filterDocument 필터 (int[])
    if (body.filterDocument?.length > 0) {
      const allowedSaveTypes = new Set<number>(body.filterDocument as number[]);
      logResults = logResults.filter((l) => allowedSaveTypes.has(l.saveType));
    }
    // workgroupIdList 필터
    if (body.workgroupIdList?.length > 0) {
      logResults = logResults.filter((l) =>
        body.workgroupIdList.includes(l.workgroupId)
      );
    }
    // 날짜 범위 필터
    if (body.startDate) {
      logResults = logResults.filter((l) => l.logDate >= body.startDate);
    }
    if (body.endDate) {
      logResults = logResults.filter((l) => l.logDate <= body.endDate);
    }

    // 정렬
    const order: string = body.order ?? "datedesc";
    logResults = [...logResults].sort((a, b) => {
      switch (order) {
        case "date":
          return a.logDate - b.logDate;
        case "file":
          return a.documnetName.localeCompare(b.documnetName);
        case "filedesc":
          return b.documnetName.localeCompare(a.documnetName);
        case "datedesc":
        default:
          return b.logDate - a.logDate;
      }
    });
    return HttpResponse.json({
      logResults,
      total: logResults.length,
      logId: "demo-me-cursor-001",
      pageTotal: 1,
      posPage: 1,
      pageRange: 20,
      rowRange: 20,
    });
  }),

  // GET /wapi/logs/doc/{syncId}/{cursor}/{revisionNo}] - getMemos (WrapsodySetRevisionMemoResult)
  http.get(/\/wapi\/logs\/doc\/([^/]+)/, async ({ request }) => {
    await delay(50);
    const url = new URL(request.url);
    const parts = url.pathname.split("/wapi/logs/doc/")[1]?.split("/") ?? [];
    const syncId = parts[0] ?? "";
    const doc = findDoc(syncId);
    const comments = getComments(syncId);
    const signs = comments.map((c) => ({
      syncId: c.syncId,
      revisionNo: c.revisionNo,
      userId: c.userId,
      userName: c.userName,
      memo: c.memo,
      signDate: c.regDate,
      userType: c.userType,
    }));
    return HttpResponse.json({
      ver: "1.0",
      syncId,
      total: comments.length,
      nextcursor: -1,
      nextRevisionNo: -1,
      revisions: {
        count: 1,
        revision: [
          {
            no: doc?.revisionNo ?? 1,
            contentName: doc?.contentName ?? "",
            memo: "",
            regDate: doc?.revisionDate ?? Date.now(),
            checkinUser: {
              userId: doc?.master?.userId ?? demoUserId,
              userName: doc?.master?.userName ?? "Demo User",
            },
            delStatus: "N",
            fileSize: doc?.fileSize ?? 0,
            signs: { count: signs.length, sign: signs },
            concurrentFlag: "",
            concurrentUserList: [],
          },
        ],
      },
    });
  }),

  // ──────────────────────────────────────────
  // 알림 설정 (GET/POST /wapi/policy/notification/myInfo)
  // ──────────────────────────────────────────
  http.get(rx("/wapi/policy/notification/myInfo"), async () => {
    await delay(100);
    return HttpResponse.json(store_noti);
  }),
  http.post(rx("/wapi/policy/notification/myInfo"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    if (body.notificationPolicy) {
      store_noti = { ...store_noti, ...body.notificationPolicy };
    }
    return HttpResponse.json(store_noti);
  }),

  // ──────────────────────────────────────────
  // 문서 권한 실행 (POST /wapi/policy/{syncId}/{workgroupId}/execute)
  // ──────────────────────────────────────────
  http.post(
    rxPath("/wapi/policy", ":syncId", ":workgroupId", "execute"),
    async ({ request, params }) => {
      await delay(50);
      const syncId = capture(params, 2);
      const body: any = await request.json().catch(() => ({}));
      const policyList: any[] = body.policyList ?? [];
      const doc = findDoc(syncId) ?? store_docs[0];
      const current = getDocPermissions(syncId) ?? buildDefaultPermissions(doc);
      const now = Date.now();

      let updated = [...current];
      policyList.forEach((item) => {
        const member = demoMembers.find((m) => m.userId === item.targetValue);
        switch (item.changeCode) {
          case "CREATE": {
            const uid = `${now.toString(16)}${item.targetValue
              .replace(/\W/g, "")
              .slice(0, 8)}`;
            updated.push({
              ...item,
              changeCode: null,
              uniqueId: uid,
              policyCode: uid,
              updateTime: now,
              updateUser: demoUserId,
              companyName: "Wrapsody eCo Demo",
              deptName: member?.deptName ?? "",
              userType: member?.userType ?? 1,
              status: member?.status ?? 1,
              userName: member?.userName ?? item.targetValue,
              phoneNumber: "",
            });
            break;
          }
          case "UPDATE": {
            const idx = updated.findIndex(
              (p) => p.targetValue === item.targetValue
            );
            if (idx !== -1)
              updated[idx] = {
                ...updated[idx],
                ...item,
                changeCode: null,
                updateTime: now,
              };
            break;
          }
          case "DELETE": {
            updated = updated.filter((p) => p.targetValue !== item.targetValue);
            break;
          }
        }
      });

      setDocPermissions(syncId, updated);
      return HttpResponse.json(updated);
    }
  ),

  // ──────────────────────────────────────────
  // 링크 권한 (GET/POST /wapi/policy/actionUrl/:workgroupId/:syncId)
  // ──────────────────────────────────────────
  http.get(
    rxPath("/wapi/policy/actionUrl", ":workgroupId", ":syncId"),
    async ({ params }) => {
      await delay(100);
      const syncId = capture(params, 3);
      return HttpResponse.json({
        fileShareLinkPolicy: getLinkPolicy(syncId),
        actionUrl: `https://plz28.test.wrapsodyeco.com/action/byuid/demo-${syncId}`,
      });
    }
  ),
  http.post(
    rxPath("/wapi/policy/actionUrl", ":workgroupId", ":syncId"),
    async ({ request, params }) => {
      await delay(50);
      const syncId = capture(params, 3);
      const body: any = await request.json().catch(() => ({}));
      const policy = body.fileShareLinkPolicy ?? body;
      const updated = setLinkPolicy(syncId, policy);
      return HttpResponse.json({
        fileShareLinkPolicy: updated,
        actionUrl: `https://plz28.test.wrapsodyeco.com/action/byuid/demo-${syncId}`,
      });
    }
  ),

  // ──────────────────────────────────────────
  // 의견 추가 (POST /wapi/contents/comment)
  // ──────────────────────────────────────────
  http.post(rx("/wapi/contents/comment"), async ({ request }) => {
    await delay(50);
    const body: any = await request.json().catch(() => ({}));
    const { syncId, memo, revisionNo } = body;
    const doc = findDoc(syncId);
    addComment(
      syncId,
      memo ?? "",
      revisionNo ?? 1,
      doc?.master?.userId ?? demoUserId,
      doc?.master?.userName ?? "Demo User",
      1
    );
    incrementDocSignCount(syncId);
    return HttpResponse.json({ ver: "1.0", syncId, revisionNo });
  }),

  // ──────────────────────────────────────────
  // 채팅 URL GET /wapi/chat/url/:workgroupId
  // ──────────────────────────────────────────
  http.get(/\/wapi\/chat\/url\/([^/]+)/, async ({ params }) => {
    await delay(100);
    const workgroupId = (Object.values(params)[0] as string) ?? "";
    const isNew = !demoWorkgroups.some((w) => w.workgroupId === workgroupId);
    const wg = store_workgroups.find((w) => w.workgroupId === workgroupId);
    const memberList = (wg?.members ?? demoMembers)
      .filter((m) => m.userId !== demoUserId)
      .map((m) => ({
        userId: m.userId,
        userName: m.userName ?? m.userId,
        initials: (m.userName ?? "?")
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2),
        userType: m.userType,
      }));
    const mParam = encodeURIComponent(
      btoa(encodeURIComponent(JSON.stringify(memberList)))
    );
    const query = isNew
      ? `?workgroupId=${workgroupId}&empty=1&m=${mParam}`
      : `?workgroupId=${workgroupId}&m=${mParam}`;
    return HttpResponse.text(
      `${window.location.origin}/demo-chat.html${query}`
    );
  }),

  // ──────────────────────────────────────────
  // 채팅 페이지용 워크그룹 멤버 목록
  // GET /wapi/chat/members/:workgroupId
  // ──────────────────────────────────────────
  http.get(/\/wapi\/chat\/members\/([^/]+)/, async ({ params }) => {
    await delay(80);
    const workgroupId = (Object.values(params)[0] as string) ?? "";
    const wg = store_workgroups.find((w) => w.workgroupId === workgroupId);
    const members = wg?.members ?? demoMembers;
    return HttpResponse.json(
      members.map((m) => ({
        userId: m.userId,
        userName: m.userName,
        initials: (m.userName ?? "?")
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2),
        userType: m.userType,
      }))
    );
  }),

  // ──────────────────────────────────────────
  // Catch-all: /wapi/* /papi/* 미처리 요청 → 401 방지
  // (더 구체적인 핸들러가 없을 때 실제 서버로 가는 것을 막음)
  // ──────────────────────────────────────────
  http.all(/\/(?:wapi|papi)\//, async ({ request }) => {
    await delay(50);
    if (request.method === "GET") {
      return HttpResponse.json({});
    }
    return HttpResponse.json({ code: "200", message: "OK" });
  }),
];
